import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import {
  Building2, Save, Upload, Trash2, LogOut, ExternalLink, ImagePlus, ArrowLeft,
  Phone, Mail, User, MapPin, Users, Tag, Loader2, Eye, Crown, CheckCircle2, Clock, Sparkles, Check, AlertTriangle, Home, Settings, ClipboardList, Lock, UserPlus, CreditCard, Megaphone, Send, ChevronDown, Globe, Facebook, Twitter, Instagram, Youtube, BarChart3, MessageCircle, Star, StickyNote, X,
} from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import ClaimBusiness from "../components/ClaimBusiness";
import { SortableGallery } from "../components/SortableGallery";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { useToast } from "../hooks/use-toast";
import { useAuth } from "../context/AuthContext";
import { useCategories } from "../context/CategoriesContext";
import { CITIES } from "../mock/mockData";
import { districtsOf } from "../lib/districts";
import StatsPanel from "../components/StatsPanel";
import { AdminFirmStatus } from "../components/AdminFirmStatus";

const PLANS = [
  { id: "1-ay", label: "1 Aylık", months: 1, tag: "" },
  { id: "3-ay", label: "3 Aylık", months: 3, tag: "Popüler" },
  { id: "6-ay", label: "6 Aylık", months: 6, tag: "" },
  { id: "12-ay", label: "1 Yıllık", months: 12, tag: "En Avantajlı" },
];

const SECTIONS = [
  { id: "home", label: "Ana Sayfa", icon: Home },
  { id: "info", label: "İşletme Bilgileri", icon: Building2 },
  { id: "teklifler-group", label: "Teklifler", icon: Mail },
  { id: "reklam-group", label: "Reklam Talepleri", icon: Megaphone },
  { id: "payments", label: "Ödemeler", icon: CreditCard },
  { id: "settings", label: "Ayarlar", icon: Settings },
];

const FEATURE_OPTIONS = [
  "Kapalı Salon", "Açık Alan", "Kır Bahçesi", "Orkestra", "DJ", "Yemekli",
  "Otopark", "Vale", "Işık Gösterisi", "Sahne Şovu", "Kına Alanı", "VIP Loca",
  "Dekorasyon", "Nikah Alanı", "Konaklama", "Deniz Manzarası",
];

// Resize + compress an image file to a small JPEG dataURL
function fileToDataURL(file, maxW = 1280, quality = 0.72) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const scale = Math.min(1, maxW / img.width);
        const w = Math.round(img.width * scale);
        const h = Math.round(img.height * scale);
        const canvas = document.createElement("canvas");
        canvas.width = w; canvas.height = h;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, w, h);
        resolve(canvas.toDataURL("image/jpeg", quality));
      };
      img.onerror = reject;
      img.src = e.target.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

const DiscountBox = ({ API, token, basePrice, paymentType, applied, onApply, onClear, testid }) => {
  const { toast } = useToast();
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const fmt = (v) => new Intl.NumberFormat("tr-TR").format(v) + " ₺";
  const validate = async () => {
    if (!code.trim()) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/discount-codes/validate`, { code: code.trim(), amount: Number(basePrice), paymentType }, { headers: { Authorization: `Bearer ${token}` } });
      onApply({ code: r.data.code, discountedAmount: r.data.discountedAmount, discountAmount: r.data.discountAmount });
      toast({ title: "İndirim uygulandı", description: `Yeni tutar: ${fmt(r.data.discountedAmount)}` });
      setCode("");
    } catch (e) {
      toast({ title: "Geçersiz kod", description: e?.response?.data?.detail || "Kod uygulanamadı." });
    } finally { setBusy(false); }
  };
  if (applied) {
    return (
      <div className="mt-4 flex items-center justify-between gap-2 rounded-xl border border-[#B9E3C6] bg-[#F3FAF5] px-3 py-2" data-testid={`${testid}-applied`}>
        <span className="text-sm text-[#237045] font-medium flex items-center gap-1.5">
          <Tag className="w-4 h-4" /> {applied.code} uygulandı · −{fmt(applied.discountAmount)} → {fmt(applied.discountedAmount)}
        </span>
        <button onClick={onClear} data-testid={`${testid}-clear`} className="text-[#9E2A3B] text-sm hover:underline">Kaldır</button>
      </div>
    );
  }
  return (
    <div className="mt-4 flex items-center gap-2">
      <input
        value={code}
        onChange={(e) => setCode(e.target.value.toUpperCase())}
        onKeyDown={(e) => { if (e.key === "Enter") validate(); }}
        data-testid={testid}
        placeholder="İndirim kodu"
        className="h-10 px-3 rounded-xl bg-[#FBF5F0] border border-[#F0DDE0] outline-none text-sm text-[#2A2224] uppercase w-40 focus:border-[#C15B6B]"
      />
      <button onClick={validate} disabled={busy || !code.trim()} data-testid={`${testid}-apply`} className="h-10 px-4 rounded-xl border border-[#C15B6B] text-[#9E2A3B] text-sm font-medium hover:bg-[#FBF0F2] transition-colors disabled:opacity-60">
        {busy ? "..." : "Uygula"}
      </button>
    </div>
  );
};


const FirmaPanel = ({ adminFirmId = null }) => {
  const adminMode = !!adminFirmId;
  const auth = useAuth();
  const { categories: CATEGORIES } = useCategories();
  const navigate = useNavigate();
  const { toast } = useToast();
  const fileRef = useRef(null);

  const API = auth.API;
  const [adminFirm, setAdminFirm] = useState(null);
  const [adminLoading, setAdminLoading] = useState(adminMode);
  const adminToken = adminMode ? (localStorage.getItem("ds_admin_token") || "") : "";
  const [adminNotes, setAdminNotes] = useState([]);
  const [adminAdReqs, setAdminAdReqs] = useState([]);
  const [newNote, setNewNote] = useState("");
  const [notesBusy, setNotesBusy] = useState(false);

  const loadAdminFirm = useCallback(async () => {
    if (!adminMode) return;
    setAdminLoading(true);
    try {
      const r = await axios.get(`${API}/admin/firms/${adminFirmId}/detail`, { headers: { Authorization: `Bearer ${adminToken}` } });
      setAdminFirm(r.data);
    } catch (e) {
      toast({ title: "Hata", description: "Firma getirilemedi." });
      navigate("/patron");
    } finally { setAdminLoading(false); }
    // eslint-disable-next-line
  }, [adminMode, adminFirmId, API]);

  useEffect(() => { if (adminMode) loadAdminFirm(); }, [adminMode, loadAdminFirm]);

  const firm = adminMode ? adminFirm : auth.firm;
  const token = adminMode ? adminToken : auth.token;
  const loading = adminMode ? adminLoading : auth.loading;
  const setFirm = adminMode ? setAdminFirm : auth.setFirm;
  const logout = adminMode ? () => navigate("/patron") : auth.logout;
  const refresh = adminMode ? loadAdminFirm : auth.refresh;
  const meBase = adminMode ? `/admin/firms/${adminFirmId}` : "/firms/me";

  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [section, setSection] = useState(adminFirmId ? "info" : "home");
  const [openGroup, setOpenGroup] = useState(adminFirmId ? "info" : "teklifler");
  const [reviews, setReviews] = useState([]);
  const [settingsTab, setSettingsTab] = useState("contact");
  const [infoTab, setInfoTab] = useState("main");
  const [featPlan, setFeatPlan] = useState("1-ay");
  const [featBusy, setFeatBusy] = useState(false);
  const [pkgPrices, setPkgPrices] = useState({});
  const [hlPkgPrices, setHlPkgPrices] = useState({});
  const [campaigns, setCampaigns] = useState({ featured: { active: false, percent: 0 }, highlight: { active: false, percent: 0 } });
  const [hlPlan, setHlPlan] = useState("1-ay");
  const [discounts, setDiscounts] = useState({ membership: null, featured: null, highlight: null, category: null });
  const setDiscount = (key, val) => setDiscounts((p) => ({ ...p, [key]: val }));
  const [membership, setMembership] = useState({ price: 0, durationMonths: 12, premiumImageLimit: 20, freeImageLimit: 5, configured: false });
  const [payBusy, setPayBusy] = useState(false);
  const [optsByGroup, setOptsByGroup] = useState({});
  const [customFeature, setCustomFeature] = useState("");
  const [firmPassword, setFirmPassword] = useState("");
  const [settingPassword, setSettingPassword] = useState(false);
  const [extraCustomFeature, setExtraCustomFeature] = useState({});
  const [activeExtraSlug, setActiveExtraSlug] = useState(null);
  const [quotes, setQuotes] = useState([]);
  const [subUsers, setSubUsers] = useState([]);
  const [newUser, setNewUser] = useState({ name: "", phone: "", password: "" });
  const [userBusy, setUserBusy] = useState(false);
  const [payments, setPayments] = useState([]);
  const [adForm, setAdForm] = useState({ budget: "", notes: "" });
  const [adBusy, setAdBusy] = useState(false);
  const [myAdRequests, setMyAdRequests] = useState([]);
  const [memberAdForm, setMemberAdForm] = useState({ budget: "", notes: "" });
  const [memberAdBusy, setMemberAdBusy] = useState(false);
  const [myMemberAdRequests, setMyMemberAdRequests] = useState([]);
  const [instaAdForm, setInstaAdForm] = useState({ budget: "", notes: "" });
  const [instaAdBusy, setInstaAdBusy] = useState(false);
  const [myInstaAdRequests, setMyInstaAdRequests] = useState([]);

  useEffect(() => {
    if (!token) return;
    if (adminMode) {
      axios.get(`${API}/admin/firms/${adminFirmId}/payments`, { headers: { Authorization: `Bearer ${token}` } }).then((r) => setPayments(r.data || [])).catch(() => {});
      axios.get(`${API}/admin/firms/${adminFirmId}/notes`, { headers: { Authorization: `Bearer ${token}` } }).then((r) => setAdminNotes(r.data || [])).catch(() => {});
      axios.get(`${API}/admin/firms/${adminFirmId}/ad-requests`, { headers: { Authorization: `Bearer ${token}` } }).then((r) => setAdminAdReqs(r.data || [])).catch(() => {});
      return;
    }
    axios.get(`${API}/firms/me/quotes`, { headers: { Authorization: `Bearer ${token}` } }).then((r) => setQuotes(r.data || [])).catch(() => {});
    axios.get(`${API}/firms/me/reviews`, { headers: { Authorization: `Bearer ${token}` } }).then((r) => setReviews(r.data || [])).catch(() => {});
    axios.get(`${API}/firms/me/users`, { headers: { Authorization: `Bearer ${token}` } }).then((r) => setSubUsers(r.data || [])).catch(() => {});
    axios.get(`${API}/firms/me/payments`, { headers: { Authorization: `Bearer ${token}` } }).then((r) => setPayments(r.data || [])).catch(() => {});
    axios.get(`${API}/firms/me/google-ad-requests`, { headers: { Authorization: `Bearer ${token}` } }).then((r) => setMyAdRequests(r.data || [])).catch(() => {});
    axios.get(`${API}/firms/me/member-ad-requests`, { headers: { Authorization: `Bearer ${token}` } }).then((r) => setMyMemberAdRequests(r.data || [])).catch(() => {});
    axios.get(`${API}/firms/me/instagram-ad-requests`, { headers: { Authorization: `Bearer ${token}` } }).then((r) => setMyInstaAdRequests(r.data || [])).catch(() => {});
  }, [token, API, adminMode]);

  const authHeaders = { Authorization: `Bearer ${token}` };
  const reloadReviews = () =>
    axios.get(`${API}/firms/me/reviews`, { headers: authHeaders }).then((r) => setReviews(r.data || [])).catch(() => {});
  const approveReview = async (id) => {
    try {
      await axios.put(`${API}/firms/me/reviews/${id}/approve`, {}, { headers: authHeaders });
      toast({ title: "Yayınlandı", description: "Yorum sitede yayınlandı." });
      reloadReviews();
    } catch { toast({ title: "Hata", description: "İşlem başarısız." }); }
  };
  const deleteReview = async (id) => {
    if (!window.confirm("Bu yorumu silmek istediğinize emin misiniz?")) return;
    try {
      await axios.delete(`${API}/firms/me/reviews/${id}`, { headers: authHeaders });
      toast({ title: "Silindi", description: "Yorum kaldırıldı." });
      reloadReviews();
    } catch { toast({ title: "Hata", description: "Silinemedi." }); }
  };
  const [extraCat, setExtraCat] = useState("");
  const [extraEdits, setExtraEdits] = useState({});
  const [extraSaving, setExtraSaving] = useState("");
  const [extraUploading, setExtraUploading] = useState("");
  const extraFileRefs = useRef({});

  useEffect(() => {
    axios.get(`${API}/ad-packages`).then((r) => setPkgPrices(r.data || {})).catch(() => {});
    axios.get(`${API}/highlight-packages`).then((r) => setHlPkgPrices(r.data || {})).catch(() => {});
    axios.get(`${API}/campaigns`).then((r) => setCampaigns(r.data || {})).catch(() => {});
    axios.get(`${API}/membership`).then((r) => setMembership((p) => ({ ...p, ...(r.data || {}) }))).catch(() => {});
  }, []);

  const isPremium = !!firm?.isPremium || adminMode;
  const imgLimit = adminMode ? (membership.premiumImageLimit || 20) : (isPremium ? (membership.premiumImageLimit || 20) : (membership.freeImageLimit || 5));

  const groupOf = (slug) => (CATEGORIES.find((c) => c.slug === slug) || {}).group || "firma";
  const firmGroup = groupOf(form?.category || firm?.category);
  const optsFor = (slug) => optsByGroup[groupOf(slug)] || (groupOf(slug) === "mekan" ? FEATURE_OPTIONS : []);
  const featureOptions = optsFor(form?.category || firm?.category);
  const setFeatureOptions = (opts) => setOptsByGroup((m) => ({ ...m, [firmGroup]: opts }));

  useEffect(() => {
    ["mekan", "firma"].forEach((g) => axios.get(`${API}/feature-options`, { params: { group: g } })
      .then((r) => { if (Array.isArray(r.data?.options) && r.data.options.length) setOptsByGroup((m) => ({ ...m, [g]: r.data.options })); })
      .catch(() => {}));
  }, [API]);
  const groupPrice = Number(firmGroup === "mekan" ? membership.priceMekan : membership.priceFirma) || 0;
  const membershipPrice = groupPrice > 0 ? groupPrice : (Number(membership.price) || 0);

  const extraVal = (cat, key, fallback = "") => {
    const e = extraEdits[cat];
    if (e && e[key] !== undefined) return e[key];
    const entry = (firm?.extraCategories || []).find((x) => x.category === cat);
    const v = entry?.info?.[key];
    return v === undefined || v === null ? fallback : v;
  };
  const setExtraVal = (cat, key, val) => setExtraEdits((p) => ({ ...p, [cat]: { ...(p[cat] || {}), [key]: val } }));

  const extraFeatures = (cat) => {
    const e = extraEdits[cat];
    if (e && e.features !== undefined) return e.features;
    const entry = (firm?.extraCategories || []).find((x) => x.category === cat);
    return entry?.info?.features || [];
  };
  const toggleExtraFeature = (cat, f) => {
    const cur = extraFeatures(cat);
    const next = cur.includes(f) ? cur.filter((x) => x !== f) : [...cur, f];
    setExtraVal(cat, "features", next);
  };
  const addExtraCustomFeature = (cat) => {
    const val = (extraCustomFeature[cat] || "").trim();
    if (!val) return;
    const cur = extraFeatures(cat);
    if (!cur.some((x) => x.toLowerCase() === val.toLowerCase())) {
      setExtraVal(cat, "features", [...cur, val]);
    }
    setExtraCustomFeature((p) => ({ ...p, [cat]: "" }));
  };

  const saveExtra = async (cat) => {
    setExtraSaving(cat);
    try {
      const body = {
        name: extraVal(cat, "name"), description: extraVal(cat, "description"),
        capacity: extraVal(cat, "capacity"), priceFrom: Number(extraVal(cat, "priceFrom")) || 0,
        contactName: extraVal(cat, "contactName"), publicPhone: extraVal(cat, "publicPhone"),
        features: extraFeatures(cat),
      };
      const res = await axios.put(`${API}${meBase}/extra-categories/${cat}`, body, { headers });
      setFirm(res.data);
      toast({ title: "Kaydedildi", description: "Ek kategori bilgileri güncellendi." });
    } catch (e) { toast({ title: "Hata", description: e?.response?.data?.detail || "Kaydedilemedi." }); }
    finally { setExtraSaving(""); }
  };

  const handleExtraFiles = async (cat, e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setExtraUploading(cat);
    try {
      const urls = [];
      for (const f of files.slice(0, 10)) { urls.push(await fileToDataURL(f)); }
      const res = await axios.post(`${API}${meBase}/extra-categories/${cat}/images`, { images: urls }, { headers });
      setFirm(res.data);
      toast({ title: "Yüklendi", description: `${urls.length} fotoğraf eklendi.` });
    } catch (err) { toast({ title: "Hata", description: err?.response?.data?.detail || "Yüklenemedi." }); }
    finally { setExtraUploading(""); if (extraFileRefs.current[cat]) extraFileRefs.current[cat].value = ""; }
  };

  const startPayment = async (type = "membership", category = null, plan = null, discountCode = null) => {
    setPayBusy(true);
    try {
      const res = await axios.post(`${API}/payments/initialize`, { returnBase: window.location.origin, type, category, plan, discountCode }, { headers: { Authorization: `Bearer ${token}` } });
      if (res.data.paymentPageUrl) { window.location.href = res.data.paymentPageUrl; return; }
      if (res.data.checkoutFormContent) {
        document.open();
        document.write(res.data.checkoutFormContent);
        document.close();
        return;
      }
      toast({ title: "Hata", description: "Ödeme sayfası açılamadı." });
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (status === 403) { try { await refresh?.(); } catch (_) {} }
      toast({ title: "Ödeme başlatılamadı", description: detail || "Bir hata oluştu, lütfen tekrar deneyin." });
    } finally { setPayBusy(false); }
  };


  const fmtPrice = (v) => (v ? new Intl.NumberFormat("tr-TR").format(v) + " ₺" : "");

  const campActive = (kind) => { const c = (campaigns || {})[kind] || {}; return !!c.active && Number(c.percent) > 0; };
  const campPct = (kind) => Number(((campaigns || {})[kind] || {}).percent || 0);
  const campPrice = (kind, price) => Math.max(0, Math.round(Number(price) * (1 - campPct(kind) / 100)));
  const campDaysLeft = (kind) => {
    const c = (campaigns || {})[kind] || {};
    if (!c.end) return null;
    const end = new Date(c.end + "T23:59:59");
    const diff = Math.ceil((end.getTime() - Date.now()) / 86400000);
    return diff >= 0 ? diff : null;
  };
  const CampaignCountdown = ({ kind }) => {
    if (!campActive(kind)) return null;
    const d = campDaysLeft(kind);
    return (
      <div data-testid={`campaign-countdown-${kind}`} className="mb-4 flex items-center gap-2 rounded-xl bg-gradient-to-r from-[#FFF4E6] to-[#FBEEF0] border border-[#E7A9B0] px-4 py-2.5">
        <Sparkles className="w-4 h-4 text-[#9E2A3B] shrink-0" />
        <p className="text-sm text-[#9E2A3B] font-medium">
          %{campPct(kind)} kampanya indirimi!
          {d !== null && (d <= 3
            ? <span className="ml-1 font-bold">Son {d === 0 ? "gün" : `${d} gün`}! Fırsatı kaçırmayın.</span>
            : <span className="ml-1">Bitişine {d} gün kaldı.</span>)}
        </p>
      </div>
    );
  };

  const daysLeft = (() => {
    if (!firm?.featured || !firm?.featuredUntil) return null;
    return Math.ceil((new Date(firm.featuredUntil).getTime() - Date.now()) / 86400000);
  })();
  const expiringSoon = daysLeft !== null && daysLeft <= 10 && daysLeft >= 0;
  const premiumDaysLeft = (() => {
    if (!isPremium || !firm?.premiumUntil) return null;
    return Math.ceil((new Date(firm.premiumUntil).getTime() - Date.now()) / 86400000);
  })();
  const premiumExpiringSoon = premiumDaysLeft !== null && premiumDaysLeft <= 10 && premiumDaysLeft >= 0;
  const highlightDaysLeft = (() => {
    if (!firm?.highlightedUntil) return null;
    const d = Math.ceil((new Date(firm.highlightedUntil).getTime() - Date.now()) / 86400000);
    return d >= 0 ? d : null;
  })();
  const isHighlighted = highlightDaysLeft !== null;
  const showPlans = isPremium && !firm?.featureRequested && (!firm?.featured || expiringSoon);

  const requestFeature = async () => {
    setFeatBusy(true);
    try {
      const res = await axios.post(`${API}/firms/me/feature-request`, { plan: featPlan }, { headers: { Authorization: `Bearer ${token}` } });
      setFirm(res.data);
      toast({ title: "Talebiniz alındı!", description: "Ödeme sonrası işletmeniz ana sayfada öne çıkarılacaktır." });
    } catch {
      toast({ title: "Hata", description: "Talep gönderilemedi." });
    } finally { setFeatBusy(false); }
  };

  const cancelFeature = async () => {
    setFeatBusy(true);
    try {
      const res = await axios.delete(`${API}/firms/me/feature-request`, { headers: { Authorization: `Bearer ${token}` } });
      setFirm(res.data);
      toast({ title: "Talep iptal edildi" });
    } catch {} finally { setFeatBusy(false); }
  };

  const submitGoogleAd = async () => {
    if (!adForm.budget.trim() && !adForm.notes.trim()) {
      toast({ title: "Eksik bilgi", description: "Lütfen aylık bütçe veya bir not girin." });
      return;
    }
    setAdBusy(true);
    try {
      const res = await axios.post(`${API}/firms/me/google-ad-request`, { budget: adForm.budget.trim(), notes: adForm.notes.trim() }, { headers: { Authorization: `Bearer ${token}` } });
      setMyAdRequests((p) => [res.data, ...p]);
      setAdForm({ budget: "", notes: "" });
      toast({ title: "Talebiniz alındı!", description: "Google reklam talebiniz patron ekibine iletildi. En kısa sürede sizinle iletişime geçilecektir." });
    } catch (e) {
      toast({ title: "Hata", description: e?.response?.data?.detail || "Talep gönderilemedi." });
    } finally { setAdBusy(false); }
  };

  const submitMemberAd = async () => {
    if (!memberAdForm.budget.trim() && !memberAdForm.notes.trim()) {
      toast({ title: "Eksik bilgi", description: "Lütfen aylık bütçe veya bir not girin." });
      return;
    }
    setMemberAdBusy(true);
    try {
      const res = await axios.post(`${API}/firms/me/member-ad-request`, { budget: memberAdForm.budget.trim(), notes: memberAdForm.notes.trim() }, { headers: { Authorization: `Bearer ${token}` } });
      setMyMemberAdRequests((p) => [res.data, ...p]);
      setMemberAdForm({ budget: "", notes: "" });
      toast({ title: "Talebiniz alındı!", description: "Üye paneli reklam talebiniz patron ekibine iletildi. En kısa sürede sizinle iletişime geçilecektir." });
    } catch (e) {
      toast({ title: "Hata", description: e?.response?.data?.detail || "Talep gönderilemedi." });
    } finally { setMemberAdBusy(false); }
  };

  const submitInstagramAd = async () => {
    if (!instaAdForm.budget.trim() && !instaAdForm.notes.trim()) {
      toast({ title: "Eksik bilgi", description: "Lütfen aylık bütçe veya bir not girin." });
      return;
    }
    setInstaAdBusy(true);
    try {
      const res = await axios.post(`${API}/firms/me/instagram-ad-request`, { budget: instaAdForm.budget.trim(), notes: instaAdForm.notes.trim() }, { headers: { Authorization: `Bearer ${token}` } });
      setMyInstaAdRequests((p) => [res.data, ...p]);
      setInstaAdForm({ budget: "", notes: "" });
      toast({ title: "Talebiniz alındı!", description: "Instagram reklam talebiniz patron ekibine iletildi. En kısa sürede sizinle iletişime geçilecektir." });
    } catch (e) {
      toast({ title: "Hata", description: e?.response?.data?.detail || "Talep gönderilemedi." });
    } finally { setInstaAdBusy(false); }
  };

  useEffect(() => {
    if (!loading && !token) navigate(adminMode ? "/patron" : "/");
  }, [loading, token, navigate, adminMode]);

  useEffect(() => {
    if (firm) {
      setForm({
        name: firm.name || "",
        category: firm.category || "dugun-salonlari",
        city: firm.city || "Ankara",
        district: firm.district || "",
        capacity: firm.capacity || "",
        priceFrom: firm.priceFrom || 0,
        priceType: firm.priceType || "",
        description: firm.description || "",
        contactName: firm.contactName || "",
        email: firm.email || "",
        publicPhone: firm.publicPhone || firm.phone || "",
        whatsappUrl: firm.whatsappUrl || "",
        smsNotifyPhone: firm.smsNotifyPhone || "",
        mapUrl: firm.mapUrl || "",
        website: firm.website || "",
        facebook: firm.facebook || "",
        twitter: firm.twitter || "",
        instagram: firm.instagram || "",
        youtube: firm.youtube || "",
        features: firm.features || [],
      });
    }
  }, [firm]);

  const headers = { Authorization: `Bearer ${token}` };

  const normalizeWhatsapp = (v) => {
    v = (v || "").trim();
    if (!v) return "";
    if (/wa\.me|whatsapp\.com|https?:\/\//i.test(v)) return v;
    const digits = v.replace(/\D/g, "");
    if (!digits) return v;
    let d = digits;
    if (d.startsWith("0")) d = "9" + d;
    else if (!d.startsWith("90")) d = "90" + d;
    return `https://wa.me/${d}`;
  };

  // "@kullanici", "instagram.com/x", "x" gibi girişleri tam URL'ye çevirir
  const normalizeSocial = (v, domain) => {
    v = (v || "").trim();
    if (!v) return "";
    let s = v.replace(/^@/, "").trim();
    if (/^https?:\/\//i.test(s)) return s;
    if (new RegExp(domain.replace(/\./g, "\\.") + "\\/", "i").test(s) || /^(www\.)?[\w-]+\.[a-z]{2,}\//i.test(s) || s.includes(domain)) {
      return "https://" + s.replace(/^www\./i, "");
    }
    return `https://${domain}/${s}`;
  };

  // Serbest URL (website): "siteniz.com" -> "https://siteniz.com"
  const normalizeUrl = (v) => {
    v = (v || "").trim();
    if (!v) return "";
    if (/^https?:\/\//i.test(v)) return v;
    return "https://" + v.replace(/^www\./i, "");
  };

  const last10 = (s) => (s || "").replace(/\D/g, "").slice(-10);
  const set = (k, v) => setForm((f) => {
    const next = { ...f, [k]: v };
    if (k === "whatsappUrl") next.waSynced = false;
    if (k === "publicPhone") {
      const synced = f.waSynced ?? (!f.whatsappUrl || last10(f.whatsappUrl) === last10(f.publicPhone));
      next.waSynced = synced;
      if (synced) next.whatsappUrl = /^5\d{9}$/.test(last10(v)) ? normalizeWhatsapp(v) : "";
    }
    return next;
  });

  const toggleFeature = (f) =>
    setForm((prev) => ({
      ...prev,
      features: prev.features.includes(f)
        ? prev.features.filter((x) => x !== f)
        : [...prev.features, f],
    }));

  const addCustomFeature = async () => {
    const val = customFeature.trim();
    if (!val) return;
    const exists = featureOptions.some((x) => x.toLowerCase() === val.toLowerCase());
    if (exists) {
      setForm((prev) =>
        prev.features.some((x) => x.toLowerCase() === val.toLowerCase())
          ? prev
          : { ...prev, features: [...prev.features, val] }
      );
      setCustomFeature("");
      return;
    }
    if (adminMode) {
      try {
        const newOpts = [...featureOptions, val];
        await axios.put(`${API}/admin/feature-options`, { options: newOpts }, { headers: { Authorization: `Bearer ${token}` }, params: { group: firmGroup } });
        setFeatureOptions(newOpts);
        setForm((prev) => ({ ...prev, features: [...prev.features, val] }));
        toast({ title: "Özellik eklendi", description: `"${val}" özellik listesine eklendi ve bu firmaya işlendi.` });
      } catch {
        toast({ title: "Hata", description: "Özellik eklenemedi." });
      }
      setCustomFeature("");
      return;
    }
    try {
      await axios.post(`${API}/firms/me/feature-add-request`, { feature: val }, { headers: { Authorization: `Bearer ${token}` } });
      toast({ title: "Talebiniz alındı", description: `"${val}" özelliği patron onayından sonra yayınlanacaktır.` });
    } catch {
      toast({ title: "Hata", description: "Özellik talebi gönderilemedi." });
    }
    setCustomFeature("");
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        ...form,
        priceFrom: Number(form.priceFrom) || 0,
        whatsappUrl: normalizeWhatsapp(form.whatsappUrl),
        website: normalizeUrl(form.website),
        facebook: normalizeSocial(form.facebook, "facebook.com"),
        twitter: normalizeSocial(form.twitter, "twitter.com"),
        instagram: normalizeSocial(form.instagram, "instagram.com"),
        youtube: normalizeSocial(form.youtube, "youtube.com"),
      };
      const res = await axios.put(`${API}${meBase}`, payload, { headers });
      setFirm(res.data);
      toast({ title: "Kaydedildi", description: "Bilgileriniz güncellendi ve sitede yayınlandı." });
    } catch (e) {
      toast({ title: "Hata", description: e?.response?.data?.detail || "Kaydedilemedi." });
    } finally {
      setSaving(false);
    }
  };

  const setFirmLoginPassword = async () => {
    const pw = firmPassword.trim();
    if (pw.length < 4) { toast({ title: "Geçersiz şifre", description: "Şifre en az 4 karakter olmalı." }); return; }
    setSettingPassword(true);
    try {
      await axios.post(`${API}/admin/firms/${adminFirmId}/set-password`, { password: pw }, { headers });
      setFirmPassword("");
      toast({ title: "Şifre belirlendi", description: "Firma bu şifreyle (giriş telefonu + şifre) panele giriş yapabilir." });
    } catch (e) {
      toast({ title: "Hata", description: e?.response?.data?.detail || "Şifre belirlenemedi." });
    } finally {
      setSettingPassword(false);
    }
  };

  const addAdminNote = async () => {
    if (!newNote.trim()) return;
    setNotesBusy(true);
    try {
      const r = await axios.post(`${API}/admin/firms/${adminFirmId}/notes`, { text: newNote.trim() }, { headers: { Authorization: `Bearer ${adminToken}` } });
      setAdminNotes((p) => [r.data, ...p]);
      setNewNote("");
      toast({ title: "Not eklendi" });
    } catch (e) { toast({ title: "Hata", description: e?.response?.data?.detail || "Not eklenemedi." }); }
    finally { setNotesBusy(false); }
  };

  const deleteAdminNote = async (noteId) => {
    try {
      await axios.delete(`${API}/admin/firms/${adminFirmId}/notes/${noteId}`, { headers: { Authorization: `Bearer ${adminToken}` } });
      setAdminNotes((p) => p.filter((n) => n.id !== noteId));
    } catch { toast({ title: "Hata", description: "Not silinemedi." }); }
  };

  const markAdminAdReq = async (reqId, status) => {
    try {
      await axios.post(`${API}/admin/ad-requests/${reqId}/status`, { status }, { headers: { Authorization: `Bearer ${adminToken}` } });
      setAdminAdReqs((p) => p.map((r) => r.id === reqId ? { ...r, status, decidedAt: new Date().toISOString() } : r));
      toast({ title: "Güncellendi", description: "Reklam talebi durumu güncellendi." });
    } catch (e) { toast({ title: "Hata", description: e?.response?.data?.detail || "Güncellenemedi." }); }
  };

  const addSubUser = async () => {    const name = newUser.name.trim();
    const phone = newUser.phone.trim();
    if (!name || !phone || !newUser.password) {
      toast({ title: "Eksik bilgi", description: "Ad, telefon ve şifre zorunludur." });
      return;
    }
    setUserBusy(true);
    try {
      const res = await axios.post(`${API}/firms/me/users`, { name, phone, password: newUser.password }, { headers });
      setSubUsers(res.data || []);
      setNewUser({ name: "", phone: "", password: "" });
      toast({ title: "Kullanıcı eklendi", description: `${name} artık panele giriş yapabilir.` });
    } catch (e) {
      toast({ title: "Hata", description: e?.response?.data?.detail || "Kullanıcı eklenemedi." });
    } finally { setUserBusy(false); }
  };

  const deleteSubUser = async (id) => {
    try {
      const res = await axios.delete(`${API}/firms/me/users/${id}`, { headers });
      setSubUsers(res.data || []);
      toast({ title: "Kullanıcı silindi" });
    } catch (e) {
      toast({ title: "Hata", description: e?.response?.data?.detail || "Silinemedi." });
    }
  };

  const handleFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const remaining = Math.max(0, imgLimit - (realImages?.length || 0));
    if (remaining <= 0) {
      toast({ title: "Limit doldu", description: `En fazla ${imgLimit} fotoğraf ekleyebilirsiniz.` });
      if (fileRef.current) fileRef.current.value = "";
      return;
    }
    setUploading(true);
    try {
      const dataUrls = [];
      for (const f of files.slice(0, remaining)) {
        // eslint-disable-next-line no-await-in-loop
        dataUrls.push(await fileToDataURL(f));
      }
      const res = await axios.post(`${API}${meBase}/images`, { images: dataUrls }, { headers });
      setFirm(res.data);
      toast({ title: "Fotoğraflar yüklendi", description: `${dataUrls.length} fotoğraf eklendi.` });
    } catch (err) {
      toast({ title: "Hata", description: err?.response?.data?.detail || "Fotoğraflar yüklenemedi." });
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const saveCaption = async (url, caption) => {
    try {
      const res = await axios.put(`${API}${meBase}/images/caption`, { url, caption }, { headers });
      setFirm(res.data);
      toast({ title: "Açıklama kaydedildi" });
    } catch (e) {
      toast({ title: "Hata", description: e?.response?.data?.detail || "Açıklama kaydedilemedi." });
    }
  };

  const reorderImages = async (order, category) => {
    try {
      const res = await axios.put(`${API}${meBase}/images/order`, { order, category }, { headers });
      setFirm(res.data);
    } catch (e) {
      toast({ title: "Hata", description: e?.response?.data?.detail || "Sıralama kaydedilemedi." });
    }
  };

  const removeImage = async (index) => {
    try {
      const res = await axios.delete(`${API}${meBase}/images/${index}`, { headers });
      setFirm(res.data);
    } catch (e) {
      toast({ title: "Hata", description: "Fotoğraf silinemedi." });
    }
  };

  if (loading || !form) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FBF5F0]">
        <Loader2 className="w-8 h-8 text-[#9E2A3B] animate-spin" />
      </div>
    );
  }

  const images = firm?.gallery && firm.gallery.length && firm.image !== firm.gallery[0]
    ? firm.gallery
    : (firm?.image ? firm.gallery : []);
  const realImages = firm?.images || firm?.gallery?.filter((g) => g && (g.startsWith("data:") || g.includes("/api/media/"))) || [];

  return (
    <div className="min-h-screen bg-[#FBF5F0]">
      <Header />

      <div className="pt-28 pb-6 bg-gradient-to-br from-[#9E2A3B] to-[#7A1F2C]">
        <div className="max-w-6xl mx-auto px-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-white/15 flex items-center justify-center">
              <Building2 className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="font-display text-2xl text-white">{firm?.name}</h1>
              <p className="text-white/80 text-sm">{adminMode ? "Patron • Firma Düzenleme" : "Firma Yönetim Paneli"}</p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap justify-end">
            {adminMode && <AdminFirmStatus firm={firm} token={adminToken} onChange={loadAdminFirm} />}
            <Link
              to={`/mekan/${firm?.slug}`}
              data-testid="firm-site-view-link"
              className="flex items-center gap-2 px-4 py-2.5 rounded-full bg-white text-[#9E2A3B] text-sm font-medium hover:bg-[#F6E4E6] transition-colors"
            >
              <Eye className="w-4 h-4" /> Sitedeki Görünümü
            </Link>
            <button
              onClick={() => { if (adminMode) { navigate("/patron"); } else { logout(); navigate("/"); } }}
              className="flex items-center gap-2 px-4 py-2.5 rounded-full border border-white/60 text-white text-sm font-medium hover:bg-white/10 transition-colors"
            >
              {adminMode ? (<><ArrowLeft className="w-4 h-4" /> Panele Dön</>) : (<><LogOut className="w-4 h-4" /> Çıkış</>)}
            </button>
          </div>
        </div>
      </div>

      {/* Sidebar + content */}
      <div className="max-w-6xl mx-auto px-5 py-8 grid grid-cols-1 lg:grid-cols-[240px_1fr] gap-6">
        {/* SIDEBAR */}
        <aside className="lg:sticky lg:top-24 h-max">
          <nav className="flex lg:flex-col gap-1.5 overflow-x-auto bg-white rounded-2xl border border-[#F0DDE0] p-2">
            {(adminMode ? SECTIONS.filter((s) => s.id === "info" || s.id === "payments") : SECTIONS).map((s) => {
              const Icon = s.icon;
              const active = section === s.id;
              if (s.id === "teklifler-group") {
                const subItems = [
                  { id: "teklifler", label: "Gelen Teklifler", icon: Mail, count: quotes.length },
                  { id: "yorumlar", label: "Gelen Yorumlar", icon: MessageCircle, count: reviews.filter((r) => r.status === "pending").length },
                ];
                const parentActive = section === "teklifler" || section === "yorumlar";
                return (
                  <div key={s.id} className="flex flex-col gap-1.5">
                    <button
                      onClick={() => setOpenGroup((g) => g === "teklifler" ? null : "teklifler")}
                      data-testid="firm-nav-teklifler-group"
                      className={`flex items-center gap-2.5 shrink-0 px-4 py-2.5 rounded-xl text-sm font-medium text-left transition-all ${parentActive ? "bg-[#9E2A3B] text-white shadow-sm" : "text-[#5b4d4f] hover:bg-[#FBF0F2] hover:text-[#9E2A3B]"}`}
                    >
                      <Icon className="w-4 h-4 shrink-0" /> {s.label}
                      <ChevronDown className={`w-4 h-4 ml-auto transition-transform ${openGroup === "teklifler" ? "rotate-180" : ""}`} />
                    </button>
                    {openGroup === "teklifler" && (
                      <div className="flex lg:flex-col gap-1 lg:pl-3 lg:border-l lg:border-[#F0DDE0] lg:ml-4" data-testid="teklifler-subnav">
                        {subItems.map((si) => {
                          const SubIcon = si.icon;
                          const subActive = section === si.id;
                          return (
                            <button
                              key={si.id}
                              onClick={() => setSection(si.id)}
                              data-testid={`firm-nav-${si.id}`}
                              className={`flex items-center gap-2 shrink-0 px-3 py-2 rounded-lg text-sm text-left transition-all ${subActive ? "bg-[#FBEEF0] text-[#9E2A3B] font-medium" : "text-[#7C6E70] hover:bg-[#FBF0F2] hover:text-[#9E2A3B]"}`}
                            >
                              <SubIcon className="w-3.5 h-3.5 shrink-0" /> {si.label}
                              {si.count > 0 && (
                                <span className="ml-auto min-w-[20px] text-center px-1.5 py-0.5 rounded-full bg-[#9E2A3B] text-white text-[11px]">{si.count}</span>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              }
              if (s.id === "reklam-group") {
                const subItems = [
                  { id: "google-ads", label: "Google Reklamı", icon: Megaphone },
                  { id: "instagram-ads", label: "Instagram Reklamı", icon: Instagram },
                  { id: "uye-ads", label: "Firman Üyelerde Görünsün", icon: Sparkles },
                ];
                const parentActive = section === "google-ads" || section === "uye-ads" || section === "instagram-ads";
                return (
                  <div key={s.id} className="flex flex-col gap-1.5">
                    <button
                      onClick={() => setOpenGroup((g) => g === "reklam" ? null : "reklam")}
                      data-testid="firm-nav-reklam-group"
                      className={`flex items-center gap-2.5 shrink-0 px-4 py-2.5 rounded-xl text-sm font-medium text-left transition-all ${parentActive ? "bg-[#9E2A3B] text-white shadow-sm" : "text-[#5b4d4f] hover:bg-[#FBF0F2] hover:text-[#9E2A3B]"}`}
                    >
                      <Icon className="w-4 h-4 shrink-0" /> {s.label}
                      <ChevronDown className={`w-4 h-4 ml-auto transition-transform ${openGroup === "reklam" ? "rotate-180" : ""}`} />
                    </button>
                    {openGroup === "reklam" && (
                      <div className="flex lg:flex-col gap-1 lg:pl-3 lg:border-l lg:border-[#F0DDE0] lg:ml-4" data-testid="reklam-subnav">
                        {subItems.map((si) => {
                          const SubIcon = si.icon;
                          const subActive = section === si.id;
                          return (
                            <button
                              key={si.id}
                              onClick={() => setSection(si.id)}
                              data-testid={`firm-nav-${si.id}`}
                              className={`flex items-center gap-2 shrink-0 px-3 py-2 rounded-lg text-sm text-left transition-all ${subActive ? "bg-[#FBEEF0] text-[#9E2A3B] font-medium" : "text-[#7C6E70] hover:bg-[#FBF0F2] hover:text-[#9E2A3B]"}`}
                            >
                              <SubIcon className="w-3.5 h-3.5 shrink-0" /> {si.label}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              }
              if (s.id === "info") {
                const subItems = [
                  { id: "main", label: "Firma Bilgileri", icon: Building2 },
                  { id: "contact", label: "İletişim Tercihleri", icon: Phone },
                  ...(adminMode ? [{ id: "yetkili", label: "Yetkili & İletişim", icon: User }] : []),
                  { id: "stats", label: "İstatistikler", icon: BarChart3 },
                ];
                return (
                  <div key={s.id} className="flex flex-col gap-1.5">
                    <button
                      onClick={() => setOpenGroup((g) => g === "info" ? null : "info")}
                      data-testid="firm-nav-info"
                      className={`flex items-center gap-2.5 shrink-0 px-4 py-2.5 rounded-xl text-sm font-medium text-left transition-all ${active ? "bg-[#9E2A3B] text-white shadow-sm" : "text-[#5b4d4f] hover:bg-[#FBF0F2] hover:text-[#9E2A3B]"}`}
                    >
                      <Icon className="w-4 h-4 shrink-0" /> {s.label}
                      <ChevronDown className={`w-4 h-4 ml-auto transition-transform ${openGroup === "info" ? "rotate-180" : ""}`} />
                    </button>
                    {openGroup === "info" && (
                      <div className="flex lg:flex-col gap-1 lg:pl-3 lg:border-l lg:border-[#F0DDE0] lg:ml-4" data-testid="info-subnav">
                        {subItems.map((si) => {
                          const SubIcon = si.icon;
                          const subActive = infoTab === si.id;
                          return (
                            <button
                              key={si.id}
                              onClick={() => { setSection("info"); setInfoTab(si.id); }}
                              data-testid={`firm-nav-info-${si.id}`}
                              className={`flex items-center gap-2 shrink-0 px-3 py-2 rounded-lg text-sm text-left transition-all ${subActive ? "bg-[#FBEEF0] text-[#9E2A3B] font-medium" : "text-[#7C6E70] hover:bg-[#FBF0F2] hover:text-[#9E2A3B]"}`}
                            >
                              <SubIcon className="w-3.5 h-3.5 shrink-0" /> {si.label}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              }
              if (s.id === "settings") {
                const subItems = [
                  { id: "contact", label: "Firma Yetkilisi", icon: User },
                  { id: "users", label: "Kullanıcılar", icon: Users },
                ];
                return (
                  <div key={s.id} className="flex flex-col gap-1.5">
                    <button
                      onClick={() => setOpenGroup((g) => g === "settings" ? null : "settings")}
                      data-testid="firm-nav-settings"
                      className={`flex items-center gap-2.5 shrink-0 px-4 py-2.5 rounded-xl text-sm font-medium text-left transition-all ${active ? "bg-[#9E2A3B] text-white shadow-sm" : "text-[#5b4d4f] hover:bg-[#FBF0F2] hover:text-[#9E2A3B]"}`}
                    >
                      <Icon className="w-4 h-4 shrink-0" /> {s.label}
                      <ChevronDown className={`w-4 h-4 ml-auto transition-transform ${openGroup === "settings" ? "rotate-180" : ""}`} />
                    </button>
                    {openGroup === "settings" && (
                      <div className="flex lg:flex-col gap-1 lg:pl-3 lg:border-l lg:border-[#F0DDE0] lg:ml-4" data-testid="settings-subnav">
                        {subItems.map((si) => {
                          const SubIcon = si.icon;
                          const subActive = settingsTab === si.id;
                          return (
                            <button
                              key={si.id}
                              onClick={() => { setSection("settings"); setSettingsTab(si.id); }}
                              data-testid={`firm-nav-settings-${si.id}`}
                              className={`flex items-center gap-2 shrink-0 px-3 py-2 rounded-lg text-sm text-left transition-all ${subActive ? "bg-[#FBEEF0] text-[#9E2A3B] font-medium" : "text-[#7C6E70] hover:bg-[#FBF0F2] hover:text-[#9E2A3B]"}`}
                            >
                              <SubIcon className="w-3.5 h-3.5 shrink-0" /> {si.label}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              }
              return (
                <button
                  key={s.id}
                  onClick={() => setSection(s.id)}
                  data-testid={`firm-nav-${s.id}`}
                  className={`flex items-center gap-2.5 shrink-0 px-4 py-2.5 rounded-xl text-sm font-medium text-left transition-all ${active ? "bg-[#9E2A3B] text-white shadow-sm" : "text-[#5b4d4f] hover:bg-[#FBF0F2] hover:text-[#9E2A3B]"}`}
                >
                  <Icon className="w-4 h-4 shrink-0" /> {s.label}
                </button>
              );
            })}
            {adminMode && (
            <button
              onClick={() => setSection("admin-ads")}
              data-testid="admin-firm-nav-ads"
              className={`flex items-center gap-2.5 shrink-0 px-4 py-2.5 rounded-xl text-sm font-medium text-left transition-all ${section === "admin-ads" ? "bg-[#9E2A3B] text-white shadow-sm" : "text-[#5b4d4f] hover:bg-[#FBF0F2] hover:text-[#9E2A3B]"}`}
            >
              <Megaphone className="w-4 h-4 shrink-0" /> Google Reklamı
              {adminAdReqs.filter((r) => (r.status || "pending") === "pending").length > 0 && (
                <span className="ml-auto text-[11px] px-1.5 py-0.5 rounded-full bg-[#3b5bdb] text-white">{adminAdReqs.filter((r) => (r.status || "pending") === "pending").length}</span>
              )}
            </button>
            )}
            {adminMode && (
            <button
              onClick={() => setSection("admin-notes")}
              data-testid="admin-firm-nav-notes"
              className={`flex items-center gap-2.5 shrink-0 px-4 py-2.5 rounded-xl text-sm font-medium text-left transition-all ${section === "admin-notes" ? "bg-[#9E2A3B] text-white shadow-sm" : "text-[#5b4d4f] hover:bg-[#FBF0F2] hover:text-[#9E2A3B]"}`}
            >
              <StickyNote className="w-4 h-4 shrink-0" /> Notlar
            </button>
            )}
            {!adminMode && (
            <>
            <div className="hidden lg:block h-px bg-[#F0DDE0] my-1.5" />
            <button
              onClick={() => setSection("claim")}
              data-testid="firm-nav-claim"
              className={`flex items-center gap-2.5 shrink-0 px-4 py-2.5 rounded-xl text-sm font-medium text-left transition-all ${section === "claim" ? "bg-[#9E2A3B] text-white shadow-sm" : "text-[#5b4d4f] hover:bg-[#FBF0F2] hover:text-[#9E2A3B]"}`}
            >
              <ClipboardList className="w-4 h-4 shrink-0" /> Firmanı Sahiplen
            </button>
            </>
            )}
          </nav>
        </aside>

        {/* CONTENT */}
        <div className="min-w-0 space-y-5">

        {(premiumExpiringSoon || expiringSoon) && (
          <div className="rounded-2xl border border-[#E7A9B0] bg-[#FBEEF0] p-4 flex flex-col sm:flex-row sm:items-center gap-3 justify-between" data-testid="renewal-banner">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-[#9E2A3B] shrink-0 mt-0.5" />
              <div className="text-sm text-[#5b4d4f]">
                {premiumExpiringSoon && <p data-testid="renewal-banner-premium"><b className="text-[#9E2A3B]">Premium üyeliğiniz {premiumDaysLeft === 0 ? "bugün" : `${premiumDaysLeft} gün sonra`} bitiyor.</b></p>}
                {expiringSoon && <p data-testid="renewal-banner-featured"><b className="text-[#9E2A3B]">Vitrin süreniz {daysLeft === 0 ? "bugün" : `${daysLeft} gün sonra`} bitiyor.</b></p>}
                <p className="mt-0.5">Kesintisiz görünmeye devam etmek için şimdi yenileyin.</p>
              </div>
            </div>
            <button
              onClick={() => { setSection("home"); setTimeout(() => document.querySelector('[data-testid="firm-membership-card"]')?.scrollIntoView({ behavior: "smooth", block: "start" }), 100); }}
              data-testid="renewal-banner-btn"
              className="shrink-0 px-5 py-2.5 rounded-xl bg-[#9E2A3B] text-white text-sm font-medium hover:bg-[#861f2f] transition-colors"
            >
              Hemen Yenile
            </button>
          </div>
        )}

        {section === "claim" && (
          <ClaimBusiness
            firm={firm}
            API={API}
            token={token}
            onClaimed={(venue, err, pending) => {
              if (err) { toast({ title: "Hata", description: err }); return; }
              if (pending) {
                toast({ title: "Talebiniz alındı", description: `${venue.name} için sahiplenme talebiniz patron onayına gönderildi. Onaylandığında ilan hesabınıza devredilecek; yeniden giriş yapmanız gerekebilir.` });
                return;
              }
              toast({ title: "İşletme sahiplenildi!", description: `${venue.name} bilgileri hesabınıza aktarıldı.` });
              setSection("info");
            }}
          />
        )}

        {section === "teklifler" && (
        <div className="bg-white rounded-2xl border border-[#F0DDE0] overflow-hidden" data-testid="quotes-section">
          <div className="flex items-center gap-3 p-6 pb-4">
            <div className="w-11 h-11 rounded-xl bg-[#FBF0F2] flex items-center justify-center shrink-0"><Mail className="w-6 h-6 text-[#9E2A3B]" /></div>
            <div>
              <h2 className="font-display text-xl text-[#2A2224]">Gelen Teklifler</h2>
              <p className="text-sm text-[#7C6E70]">Sitedeki "Ücretsiz Fiyat Teklifi Al" formundan gelen talepler. Toplam {quotes.length}.</p>
            </div>
          </div>
          {quotes.length === 0 ? (
            <div className="py-16 text-center text-[#7C6E70]" data-testid="quotes-empty"><Mail className="w-9 h-9 mx-auto mb-2 text-[#E7A9B0]" /> Henüz teklif talebi gelmedi.</div>
          ) : (
            <ul className="divide-y divide-[#F0DDE0]">
              {quotes.map((q) => (
                <li key={q.id} data-testid={`quote-${q.id}`} className="px-6 py-4 hover:bg-[#FDFAF8]">
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="font-medium text-[#2A2224]">{q.name} <a href={`tel:${q.phone}`} className="text-[#9E2A3B] font-normal">· {q.phone}</a></p>
                      {q.date && <p className="text-sm text-[#7C6E70]">Düğün Tarihi: {q.date}</p>}
                      {q.message && <p className="text-sm text-[#5b4d4f] mt-1">{q.message}</p>}
                    </div>
                    <span className="text-xs text-[#a8999b] shrink-0">{q.at ? new Date(q.at).toLocaleString("tr-TR") : ""}</span>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
        )}

        {section === "yorumlar" && (
        <div className="bg-white rounded-2xl border border-[#F0DDE0] overflow-hidden" data-testid="reviews-section">
          <div className="flex items-center gap-3 px-5 py-4 border-b border-[#F0DDE0]">
            <div className="w-10 h-10 rounded-xl bg-[#FBEEF0] flex items-center justify-center shrink-0">
              <MessageCircle className="w-5 h-5 text-[#9E2A3B]" />
            </div>
            <div>
              <h2 className="font-display text-xl text-[#2A2224]">Gelen Yorumlar</h2>
              <p className="text-sm text-[#7C6E70]">Ziyaretçilerin yazdığı yorumlar. Onayladıklarınız mekan sayfanızda yayınlanır. Toplam {reviews.length}.</p>
            </div>
          </div>
          {reviews.length === 0 ? (
            <div className="py-16 text-center text-[#7C6E70]" data-testid="reviews-empty">
              <MessageCircle className="w-9 h-9 mx-auto mb-2 text-[#E7A9B0]" /> Henüz yorum gelmedi.
            </div>
          ) : (
            <ul className="divide-y divide-[#F0DDE0]">
              {reviews.map((r) => (
                <li key={r.id} className="p-5" data-testid={`review-row-${r.id}`}>
                  <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-[#2A2224]">{r.name}</span>
                        <span className="flex items-center gap-0.5">
                          {[1, 2, 3, 4, 5].map((n) => (
                            <Star key={n} className={`w-3.5 h-3.5 ${n <= r.rating ? "text-[#C6A15B]" : "text-[#E3D6D8]"}`} fill={n <= r.rating ? "#C6A15B" : "#E3D6D8"} />
                          ))}
                        </span>
                        {r.status === "approved" ? (
                          <span className="px-2 py-0.5 rounded-full bg-[#E7F7EE] text-[#1FA855] text-[11px] font-medium">Yayında</span>
                        ) : (
                          <span className="px-2 py-0.5 rounded-full bg-[#FBF3E4] text-[#8a6d1f] text-[11px] font-medium">Onay bekliyor</span>
                        )}
                      </div>
                      <p className="text-sm text-[#5b4d4f] mt-2 leading-relaxed">{r.comment}</p>
                      <span className="text-xs text-[#a8999b] mt-1 block">{r.at ? new Date(r.at).toLocaleString("tr-TR") : ""}</span>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {r.status !== "approved" && (
                        <button onClick={() => approveReview(r.id)} data-testid={`review-approve-${r.id}`} className="flex items-center gap-1 px-3 h-9 rounded-lg bg-gradient-to-r from-[#1FA855] to-[#128C3E] text-white text-sm hover:shadow-md transition-all">
                          <Check className="w-4 h-4" /> Onayla
                        </button>
                      )}
                      <button onClick={() => deleteReview(r.id)} data-testid={`review-delete-${r.id}`} className="flex items-center gap-1 px-3 h-9 rounded-lg border border-[#F0DDE0] text-[#c0392b] text-sm hover:bg-[#FBEEF0] transition-colors">
                        <Trash2 className="w-4 h-4" /> Sil
                      </button>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
        )}

        {section === "home" && (
        <>
        <div className="mb-6 rounded-2xl border border-[#F0DDE0] bg-white p-6" data-testid="firm-membership-card">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#9E2A3B] to-[#C15B6B] flex items-center justify-center shrink-0 shadow-md">
                {isPremium ? <Sparkles className="w-6 h-6 text-white" /> : <Crown className="w-6 h-6 text-white" />}
              </div>
              <div>
                <h3 className="font-display text-xl text-[#2A2224]">Üyelik Durumu</h3>
                {isPremium ? (
                  <p className={`text-sm mt-1 flex items-center gap-1.5 ${premiumExpiringSoon ? "text-[#9E2A3B] font-medium" : "text-green-700"}`}>
                    {premiumExpiringSoon ? <AlertTriangle className="w-4 h-4 text-[#9E2A3B]" /> : <CheckCircle2 className="w-4 h-4" />} Premium üyesiniz
                    {firm?.premiumUntil && <span className="text-[#7C6E70]"> · Bitiş: {new Date(firm.premiumUntil).toLocaleDateString("tr-TR")}{premiumDaysLeft !== null && premiumDaysLeft >= 0 ? ` (${premiumDaysLeft === 0 ? "bugün" : premiumDaysLeft + " gün kaldı"})` : ""}</span>}
                  </p>
                ) : (
                  <p className="text-sm text-[#5b4d4f] mt-1 max-w-lg">
                    Ücretsiz üyelikte en fazla {membership.freeImageLimit} fotoğraf ekleyebilir, "Öne Çıkma" talebi gönderemezsiniz. Premium'a geçerek kısıtları kaldırın.
                  </p>
                )}
              </div>
            </div>
            {isPremium ? (
              <span className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-green-600 text-white text-sm font-medium shrink-0"><Sparkles className="w-4 h-4" /> Premium Aktif</span>
            ) : (
              <div className="text-left sm:text-right shrink-0">
                {membershipPrice > 0 && <p className="font-display text-2xl font-bold text-[#9E2A3B]">{fmtPrice(membershipPrice)}<span className="text-sm font-normal text-[#7C6E70]"> / {membership.durationMonths} ay</span></p>}
                <button onClick={() => startPayment("membership", null, null, discounts.membership?.code)} disabled={payBusy || !membership.configured || !membershipPrice}
                  data-testid="firm-upgrade-button"
                  className="mt-2 flex items-center gap-2 px-5 h-11 rounded-xl bg-gradient-to-r from-[#C15B6B] to-[#9E2A3B] text-white text-sm font-medium hover:shadow-md transition-all disabled:opacity-60">
                  {payBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Crown className="w-4 h-4" />}
                  Premium'a Geç
                </button>
                {!membership.configured && <p className="text-xs text-[#7C6E70] mt-1">Ödeme sistemi yakında aktif olacak.</p>}
              </div>
            )}
          </div>

          {!isPremium && membershipPrice > 0 && (
            <DiscountBox API={API} token={token} paymentType="membership" basePrice={membershipPrice} testid="discount-membership"
              applied={discounts.membership} onApply={(v) => setDiscount("membership", v)} onClear={() => setDiscount("membership", null)} />
          )}

          {premiumExpiringSoon && (
            <div data-testid="premium-renewal-reminder" className="mt-5 flex flex-col sm:flex-row sm:items-center gap-3 rounded-xl border border-[#E7A9B0] bg-[#FBEEF0] px-4 py-3">
              <AlertTriangle className="w-5 h-5 text-[#9E2A3B] shrink-0" />
              <p className="text-sm text-[#7a2531] flex-1">
                Premium üyeliğiniz {premiumDaysLeft === 0 ? "bugün doluyor" : premiumDaysLeft + " gün sonra doluyor"}. Kesintisiz devam etmek için üyeliğinizi yenileyin.
              </p>
              <button onClick={() => startPayment("membership", null, null, discounts.membership?.code)} disabled={payBusy || !membership.configured || !membershipPrice}
                data-testid="firm-renew-button"
                className="flex items-center justify-center gap-2 px-5 h-11 rounded-xl bg-gradient-to-r from-[#C15B6B] to-[#9E2A3B] text-white text-sm font-medium hover:shadow-md transition-all disabled:opacity-60 shrink-0">
                {payBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Crown className="w-4 h-4" />}
                Üyeliği Yenile
              </button>
            </div>
          )}
        </div>

        <div className="mb-6 rounded-2xl overflow-hidden border border-[#E3CE9E] bg-gradient-to-br from-[#FBF3E4] to-[#F7E8CF]">
          <div className="p-6">
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-5">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#C6A15B] to-[#a9843f] flex items-center justify-center shrink-0 shadow-md">
                  <Crown className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="font-display text-xl text-[#2A2224]">Ana Sayfada Öne Çık</h3>
                  {firm?.featured ? (
                    <p className={`text-sm mt-1 flex items-center gap-1.5 ${expiringSoon ? "text-[#9E2A3B] font-medium" : "text-[#5b4d4f]"}`}>
                      {expiringSoon ? <AlertTriangle className="w-4 h-4 text-[#9E2A3B]" /> : <CheckCircle2 className="w-4 h-4 text-green-600" />}
                      İşletmeniz şu anda ana sayfada öne çıkarılıyor
                      {firm?.featuredUntil && (
                        <span className="text-[#7C6E70]"> · Bitiş: {new Date(firm.featuredUntil).toLocaleDateString("tr-TR")}{daysLeft !== null && daysLeft >= 0 ? ` (${daysLeft === 0 ? "bugün" : daysLeft + " gün kaldı"})` : ""}</span>
                      )}
                    </p>
                  ) : firm?.featureRequested ? (
                    <p className="text-sm text-[#8a6d1f] mt-1 flex items-center gap-1.5">
                      <Clock className="w-4 h-4" /> Talebiniz alındı — ödeme onayı sonrası öne çıkarılacaksınız ({firm.featurePlan})
                    </p>
                  ) : (
                    <p className="text-sm text-[#5b4d4f] mt-1 max-w-lg">
                      Ödemeli reklam paketiyle işletmeniz ana sayfadaki "Öne Çıkan İşletmeler" bölümünde binlerce çifte gösterilsin.
                    </p>
                  )}
                </div>
              </div>

              {firm?.featured ? (
                <span className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-green-600 text-white text-sm font-medium shrink-0">
                  <Sparkles className="w-4 h-4" /> Aktif
                </span>
              ) : firm?.featureRequested ? (
                <button onClick={cancelFeature} disabled={featBusy} className="px-5 h-11 rounded-xl border border-[#b98f3a] text-[#8a6d1f] text-sm font-medium hover:bg-[#f2e4c6] transition-colors shrink-0 disabled:opacity-70">
                  {featBusy ? "..." : "Talebi İptal Et"}
                </button>
              ) : null}
            </div>

            {expiringSoon && (
              <div data-testid="renewal-reminder" className="mt-5 flex flex-col sm:flex-row sm:items-center gap-3 rounded-xl border border-[#E7A9B0] bg-[#FBEEF0] px-4 py-3">
                <AlertTriangle className="w-5 h-5 text-[#9E2A3B] shrink-0" />
                <p className="text-sm text-[#7a2531] flex-1">
                  {firm?.featureRequested
                    ? "Yenileme talebiniz alındı, ödeme onayı sonrası süreniz uzatılacaktır."
                    : `Reklam süreniz ${daysLeft === 0 ? "bugün doluyor" : daysLeft + " gün sonra doluyor"}. Kesintisiz öne çıkmaya devam etmek için aşağıdan bir paket seçip yenileyin.`}
                </p>
              </div>
            )}

            {showPlans && (
              <>
                <CampaignCountdown kind="featured" />
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mt-6">
                  {PLANS.map((p) => {
                    const active = featPlan === p.id;
                    const price = pkgPrices[p.id];
                    return (
                      <button
                        key={p.id}
                        type="button"
                        onClick={() => setFeatPlan(p.id)}
                        data-testid={`plan-card-${p.id}`}
                        className={`relative text-left rounded-2xl border-2 p-4 transition-all ${active ? "border-[#C6A15B] bg-white shadow-md -translate-y-0.5" : "border-[#EAD9B0] bg-white/60 hover:border-[#C6A15B] hover:bg-white"}`}
                      >
                        {p.tag && (
                          <span className="absolute -top-2 right-3 px-2 py-0.5 rounded-full bg-[#9E2A3B] text-white text-[10px] font-medium shadow-sm">{p.tag}</span>
                        )}
                        <div className="flex items-center justify-between">
                          <span className="font-display text-lg text-[#2A2224]">{p.label}</span>
                          <span className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${active ? "border-[#C6A15B] bg-[#C6A15B]" : "border-[#D8C08A]"}`}>
                            {active && <Check className="w-3 h-3 text-white" />}
                          </span>
                        </div>
                        <div className="mt-3">
                          {price ? (
                            campActive("featured") ? (
                              <>
                                <span className="text-sm text-[#a8999b] line-through mr-1.5">{fmtPrice(price)}</span>
                                <span className="font-display text-2xl font-bold text-[#9E2A3B]">{fmtPrice(campPrice("featured", price))}</span>
                                <span className="block text-[11px] font-semibold text-[#2E8B57] mt-0.5">%{campPct("featured")} kampanya indirimi</span>
                                {p.months > 1 && (
                                  <span className="block text-xs text-[#7C6E70] mt-0.5">≈ {fmtPrice(Math.round(campPrice("featured", price) / p.months))} / ay</span>
                                )}
                              </>
                            ) : (
                              <>
                                <span className="font-display text-2xl font-bold text-[#9E2A3B]">{fmtPrice(price)}</span>
                                {p.months > 1 && (
                                  <span className="block text-xs text-[#7C6E70] mt-0.5">≈ {fmtPrice(Math.round(price / p.months))} / ay</span>
                                )}
                              </>
                            )
                          ) : (
                            <span className="text-sm text-[#7C6E70]">Fiyat için bize ulaşın</span>
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>
                <button
                  onClick={() => startPayment("featured", null, featPlan, discounts.featured?.code)}
                  disabled={featBusy || payBusy || !pkgPrices[featPlan]}
                  data-testid="feature-request-button"
                  className="mt-5 w-full sm:w-auto flex items-center justify-center gap-2 px-6 h-12 rounded-xl bg-gradient-to-r from-[#C6A15B] to-[#a9843f] text-white text-sm font-medium hover:shadow-md transition-all disabled:opacity-70"
                >
                  {payBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Crown className="w-4 h-4" />}
                  {expiringSoon ? "Seçili Paketle Yenile" : "Seçili Paketle Öne Çık"}
                </button>
                {pkgPrices[featPlan] > 0 && (
                  <DiscountBox API={API} token={token} paymentType="featured" basePrice={pkgPrices[featPlan]} testid="discount-featured"
                    applied={discounts.featured} onApply={(v) => setDiscount("featured", v)} onClear={() => setDiscount("featured", null)} />
                )}
              </>
            )}

            {!isPremium && !firm?.featured && !firm?.featureRequested && (
              <div className="mt-6 space-y-3" data-testid="feature-price-info">
                <p className="text-sm text-[#5b4d4f]">Öne çıkma (reklam) paketi fiyatları:</p>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {PLANS.map((p) => {
                    const price = pkgPrices[p.id];
                    return (
                      <div key={p.id} data-testid={`plan-price-${p.id}`} className="relative rounded-2xl border border-[#EAD9B0] bg-white/70 p-4 text-left">
                        {p.tag && <span className="absolute -top-2 right-3 px-2 py-0.5 rounded-full bg-[#9E2A3B] text-white text-[10px] font-medium shadow-sm">{p.tag}</span>}
                        <span className="font-display text-base text-[#2A2224]">{p.label}</span>
                        <div className="mt-2">
                          {price ? (
                            <>
                              <span className="font-display text-xl font-bold text-[#9E2A3B]">{fmtPrice(price)}</span>
                              {p.months > 1 && <span className="block text-xs text-[#7C6E70] mt-0.5">≈ {fmtPrice(Math.round(price / p.months))} / ay</span>}
                            </>
                          ) : (
                            <span className="text-sm text-[#7C6E70]">Fiyat için bize ulaşın</span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div data-testid="feature-locked-notice" className="flex flex-col sm:flex-row sm:items-center gap-3 rounded-xl border border-[#E3CE9E] bg-white/70 px-4 py-3">
                  <Lock className="w-5 h-5 text-[#a9843f] shrink-0" />
                  <p className="text-sm text-[#8a6d1f] flex-1">"Öne Çıkma" özelliği yalnızca premium üyelere açıktır. Yukarıdan Premium'a geçerek işletmenizi bu paketlerle ana sayfada öne çıkarabilirsiniz.</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Firman Görünür Olsun — kategori içinde vurgulu ilan */}
        <div className="mb-6 rounded-2xl overflow-hidden border-2 border-[#C6A15B] bg-gradient-to-br from-[#FDF7EA] to-[#F6E7C6]" data-testid="highlight-section">
          <div className="p-6">
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-5">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#C6A15B] to-[#a9843f] flex items-center justify-center shrink-0 shadow-md">
                  <Sparkles className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="font-display text-xl text-[#2A2224]">Firman Görünür Olsun</h3>
                  {isHighlighted ? (
                    <p className="text-sm mt-1 flex items-center gap-1.5 text-[#5b4d4f]">
                      <CheckCircle2 className="w-4 h-4 text-green-600" />
                      İşletmeniz kategori sayfasında vurgulu (kalın yazı + altın çerçeve) görünüyor
                      {firm?.highlightedUntil && <span className="text-[#7C6E70]"> · Bitiş: {new Date(firm.highlightedUntil).toLocaleDateString("tr-TR")}{highlightDaysLeft !== null ? ` (${highlightDaysLeft === 0 ? "bugün" : highlightDaysLeft + " gün kaldı"})` : ""}</span>}
                    </p>
                  ) : (
                    <p className="text-sm text-[#5b4d4f] mt-1 max-w-lg">
                      İşletmeniz, bulunduğu kategori listesinde diğer firmalardan <strong>kalın yazı ve altın renkli çerçeveyle</strong> ayrışsın, daha çok dikkat çeksin.
                    </p>
                  )}
                </div>
              </div>
              {isHighlighted && (
                <span className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-green-600 text-white text-sm font-medium shrink-0">
                  <Sparkles className="w-4 h-4" /> Aktif
                </span>
              )}
            </div>

            {isPremium ? (
              <>
                <CampaignCountdown kind="highlight" />
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mt-6">
                  {PLANS.map((p) => {
                    const active = hlPlan === p.id;
                    const price = hlPkgPrices[p.id];
                    return (
                      <button
                        key={p.id}
                        type="button"
                        onClick={() => setHlPlan(p.id)}
                        data-testid={`highlight-plan-${p.id}`}
                        className={`relative text-left rounded-2xl border-2 p-4 transition-all ${active ? "border-[#C6A15B] bg-white shadow-md -translate-y-0.5" : "border-[#EAD9B0] bg-white/60 hover:border-[#C6A15B] hover:bg-white"}`}
                      >
                        {p.tag && <span className="absolute -top-2 right-3 px-2 py-0.5 rounded-full bg-[#9E2A3B] text-white text-[10px] font-medium shadow-sm">{p.tag}</span>}
                        <div className="flex items-center justify-between">
                          <span className="font-display text-lg text-[#2A2224]">{p.label}</span>
                          <span className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${active ? "border-[#C6A15B] bg-[#C6A15B]" : "border-[#D8C08A]"}`}>
                            {active && <Check className="w-3 h-3 text-white" />}
                          </span>
                        </div>
                        <div className="mt-3">
                          {price ? (
                            campActive("highlight") ? (
                              <>
                                <span className="text-sm text-[#a8999b] line-through mr-1.5">{fmtPrice(price)}</span>
                                <span className="font-display text-2xl font-bold text-[#9E2A3B]">{fmtPrice(campPrice("highlight", price))}</span>
                                <span className="block text-[11px] font-semibold text-[#2E8B57] mt-0.5">%{campPct("highlight")} kampanya indirimi</span>
                                {p.months > 1 && <span className="block text-xs text-[#7C6E70] mt-0.5">≈ {fmtPrice(Math.round(campPrice("highlight", price) / p.months))} / ay</span>}
                              </>
                            ) : (
                              <>
                                <span className="font-display text-2xl font-bold text-[#9E2A3B]">{fmtPrice(price)}</span>
                                {p.months > 1 && <span className="block text-xs text-[#7C6E70] mt-0.5">≈ {fmtPrice(Math.round(price / p.months))} / ay</span>}
                              </>
                            )
                          ) : (
                            <span className="text-sm text-[#7C6E70]">Fiyat için bize ulaşın</span>
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>
                <button
                  onClick={() => startPayment("highlight", null, hlPlan, discounts.highlight?.code)}
                  disabled={payBusy || !hlPkgPrices[hlPlan]}
                  data-testid="highlight-buy-button"
                  className="mt-5 w-full sm:w-auto flex items-center justify-center gap-2 px-6 h-12 rounded-xl bg-gradient-to-r from-[#C6A15B] to-[#a9843f] text-white text-sm font-medium hover:shadow-md transition-all disabled:opacity-70"
                >
                  {payBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                  {isHighlighted ? "Seçili Paketle Süreyi Uzat" : "Seçili Paketle Öne Çık"}
                </button>
                {hlPkgPrices[hlPlan] > 0 && (
                  <DiscountBox API={API} token={token} paymentType="highlight" basePrice={hlPkgPrices[hlPlan]} testid="discount-highlight"
                    applied={discounts.highlight} onApply={(v) => setDiscount("highlight", v)} onClear={() => setDiscount("highlight", null)} />
                )}
              </>
            ) : (
              <div className="mt-6 space-y-3" data-testid="highlight-price-info">
                <p className="text-sm text-[#5b4d4f]">Vurgulu ilan paketi fiyatları:</p>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {PLANS.map((p) => {
                    const price = hlPkgPrices[p.id];
                    return (
                      <div key={p.id} data-testid={`highlight-price-${p.id}`} className="relative rounded-2xl border border-[#EAD9B0] bg-white/70 p-4 text-left">
                        {p.tag && <span className="absolute -top-2 right-3 px-2 py-0.5 rounded-full bg-[#9E2A3B] text-white text-[10px] font-medium shadow-sm">{p.tag}</span>}
                        <span className="font-display text-base text-[#2A2224]">{p.label}</span>
                        <div className="mt-2">
                          {price ? (
                            <>
                              <span className="font-display text-xl font-bold text-[#9E2A3B]">{fmtPrice(price)}</span>
                              {p.months > 1 && <span className="block text-xs text-[#7C6E70] mt-0.5">≈ {fmtPrice(Math.round(price / p.months))} / ay</span>}
                            </>
                          ) : (
                            <span className="text-sm text-[#7C6E70]">Fiyat için bize ulaşın</span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div className="flex flex-col sm:flex-row sm:items-center gap-3 rounded-xl border border-[#E3CE9E] bg-white/70 px-4 py-3">
                  <Lock className="w-5 h-5 text-[#a9843f] shrink-0" />
                  <p className="text-sm text-[#8a6d1f] flex-1">"Vurgulu İlan" yalnızca premium üyelere açıktır. Yukarıdan Premium'a geçerek işletmenizi kategori sayfasında öne çıkarabilirsiniz.</p>
                </div>
              </div>
            )}
          </div>
        </div>
        </>
        )}

        {section === "google-ads" && (
        <div className="space-y-6" data-testid="google-ads-section">
          <div className="bg-white rounded-2xl border border-[#F0DDE0] p-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#4285F4] via-[#EA4335] to-[#FBBC05] flex items-center justify-center shrink-0">
                <Megaphone className="w-6 h-6 text-white" />
              </div>
              <div className="min-w-0">
                <h2 className="font-display text-xl text-[#2A2224]">Google Reklamı Talep Et</h2>
                <p className="text-sm text-[#7C6E70] mt-1">İşletmenizi Google'da arayan çiftlere üst sıralarda gösterelim. Aşağıdaki formu doldurun, reklam ekibimiz sizinle iletişime geçsin.</p>
              </div>
            </div>

            {!isPremium ? (
              <div className="mt-5 flex items-start gap-3 rounded-xl border border-[#E3CE9E] bg-[#FBF3E4] px-4 py-3" data-testid="google-ads-premium-lock">
                <Crown className="w-5 h-5 text-[#8a6d1f] shrink-0 mt-0.5" />
                <p className="text-sm text-[#8a6d1f]">Google reklamı talebi yalnızca <strong>premium üyelere</strong> açıktır. Ana Sayfa sekmesinden Premium'a geçerek bu özelliği kullanabilirsiniz.</p>
              </div>
            ) : (
              <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-[#5b4d4f] mb-1.5 block">Aylık Bütçe (₺)</Label>
                  <Input value={adForm.budget} onChange={(e) => setAdForm({ ...adForm, budget: e.target.value })} data-testid="google-ad-budget" placeholder="ör. 3000" className="border-[#F0DDE0] focus-visible:ring-[#C15B6B]" />
                </div>
                <div className="sm:col-span-2">
                  <Label className="text-[#5b4d4f] mb-1.5 block">Not / Mesaj</Label>
                  <Textarea value={adForm.notes} onChange={(e) => setAdForm({ ...adForm, notes: e.target.value })} data-testid="google-ad-notes" rows={4} placeholder="Hedef bölge, öne çıkarmak istediğiniz hizmetler veya beklentilerinizi yazın..." className="border-[#F0DDE0] focus-visible:ring-[#C15B6B]" />
                </div>
                <div>
                  <button onClick={submitGoogleAd} disabled={adBusy} data-testid="google-ad-submit" className="flex items-center justify-center gap-2 h-11 px-6 rounded-xl bg-gradient-to-r from-[#C15B6B] to-[#9E2A3B] text-white text-sm font-medium hover:shadow-md transition-all disabled:opacity-70">
                    {adBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Talep Gönder
                  </button>
                </div>
              </div>
            )}
          </div>

          {myAdRequests.length > 0 && (
            <div className="bg-white rounded-2xl border border-[#F0DDE0] overflow-hidden" data-testid="google-ads-history">
              <div className="px-6 py-4 border-b border-[#F0DDE0]">
                <h3 className="font-medium text-[#2A2224]">Talep Geçmişiniz</h3>
              </div>
              <div className="divide-y divide-[#F0DDE0]">
                {myAdRequests.map((r) => {
                  const st = r.status || "pending";
                  const badge = st === "done"
                    ? { c: "bg-[#E7F6EC] text-[#237045]", t: "İletişime geçildi / Tamamlandı" }
                    : st === "rejected"
                    ? { c: "bg-[#FBEEF0] text-[#9E2A3B]", t: "Reddedildi" }
                    : { c: "bg-[#FBF3E4] text-[#8a6d1f]", t: "Bekliyor" };
                  return (
                    <div key={r.id} className="px-6 py-4 flex flex-col sm:flex-row sm:items-center gap-2" data-testid={`google-ad-req-${r.id}`}>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          {r.budget && <span className="text-sm text-[#2A2224] font-medium">Bütçe: {r.budget} ₺</span>}
                          <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${badge.c}`}>{badge.t}</span>
                        </div>
                        {r.notes && <p className="text-sm text-[#7C6E70] mt-0.5 truncate">{r.notes}</p>}
                      </div>
                      <span className="text-xs text-[#a8999b] shrink-0">{r.createdAt ? new Date(r.createdAt).toLocaleString("tr-TR") : ""}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
        )}

        {section === "instagram-ads" && (
        <div className="space-y-6" data-testid="instagram-ads-section">
          <div className="bg-white rounded-2xl border border-[#F0DDE0] p-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#F58529] via-[#DD2A7B] to-[#8134AF] flex items-center justify-center shrink-0">
                <Instagram className="w-6 h-6 text-white" />
              </div>
              <div className="min-w-0">
                <h2 className="font-display text-xl text-[#2A2224]">Instagram Reklamı Talep Et</h2>
                <p className="text-sm text-[#7C6E70] mt-1">İşletmenizi Instagram'da düğün hazırlığındaki çiftlere tanıtalım. Aşağıdaki formu doldurun, reklam ekibimiz sizinle iletişime geçsin.</p>
              </div>
            </div>

            {!isPremium ? (
              <div className="mt-5 flex items-start gap-3 rounded-xl border border-[#E3CE9E] bg-[#FBF3E4] px-4 py-3" data-testid="instagram-ads-premium-lock">
                <Crown className="w-5 h-5 text-[#8a6d1f] shrink-0 mt-0.5" />
                <p className="text-sm text-[#8a6d1f]">Instagram reklam talebi yalnızca <strong>premium üyelere</strong> açıktır. Ana Sayfa sekmesinden Premium'a geçerek bu özelliği kullanabilirsiniz.</p>
              </div>
            ) : (
              <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-[#5b4d4f] mb-1.5 block">Aylık Bütçe (₺)</Label>
                  <Input value={instaAdForm.budget} onChange={(e) => setInstaAdForm({ ...instaAdForm, budget: e.target.value })} data-testid="instagram-ad-budget" placeholder="ör. 2000" className="border-[#F0DDE0] focus-visible:ring-[#C15B6B]" />
                </div>
                <div className="sm:col-span-2">
                  <Label className="text-[#5b4d4f] mb-1.5 block">Not / Mesaj</Label>
                  <Textarea value={instaAdForm.notes} onChange={(e) => setInstaAdForm({ ...instaAdForm, notes: e.target.value })} data-testid="instagram-ad-notes" rows={4} placeholder="Instagram hesabınız, hedef kitle, kampanyanız veya beklentilerinizi yazın..." className="border-[#F0DDE0] focus-visible:ring-[#C15B6B]" />
                </div>
                <div>
                  <button onClick={submitInstagramAd} disabled={instaAdBusy} data-testid="instagram-ad-submit" className="flex items-center justify-center gap-2 h-11 px-6 rounded-xl bg-gradient-to-r from-[#C15B6B] to-[#9E2A3B] text-white text-sm font-medium hover:shadow-md transition-all disabled:opacity-70">
                    {instaAdBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Talep Gönder
                  </button>
                </div>
              </div>
            )}
          </div>

          {myInstaAdRequests.length > 0 && (
            <div className="bg-white rounded-2xl border border-[#F0DDE0] overflow-hidden" data-testid="instagram-ads-history">
              <div className="px-6 py-4 border-b border-[#F0DDE0]">
                <h3 className="font-medium text-[#2A2224]">Talep Geçmişiniz</h3>
              </div>
              <div className="divide-y divide-[#F0DDE0]">
                {myInstaAdRequests.map((r) => {
                  const st = r.status || "pending";
                  const badge = st === "done"
                    ? { c: "bg-[#E7F6EC] text-[#237045]", t: "İletişime geçildi / Tamamlandı" }
                    : st === "rejected"
                    ? { c: "bg-[#FBEEF0] text-[#9E2A3B]", t: "Reddedildi" }
                    : { c: "bg-[#FBF3E4] text-[#8a6d1f]", t: "Bekliyor" };
                  return (
                    <div key={r.id} className="px-6 py-4 flex flex-col sm:flex-row sm:items-center gap-2" data-testid={`instagram-ad-req-${r.id}`}>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          {r.budget && <span className="text-sm text-[#2A2224] font-medium">Bütçe: {r.budget} ₺</span>}
                          <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${badge.c}`}>{badge.t}</span>
                        </div>
                        {r.notes && <p className="text-sm text-[#7C6E70] mt-0.5 truncate">{r.notes}</p>}
                      </div>
                      <span className="text-xs text-[#a8999b] shrink-0">{r.createdAt ? new Date(r.createdAt).toLocaleString("tr-TR") : ""}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
        )}

        {section === "uye-ads" && (
        <div className="space-y-6" data-testid="uye-ads-section">
          <div className="bg-white rounded-2xl border border-[#F0DDE0] p-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#C15B6B] to-[#9E2A3B] flex items-center justify-center shrink-0">
                <Sparkles className="w-6 h-6 text-white" />
              </div>
              <div className="min-w-0">
                <h2 className="font-display text-xl text-[#2A2224]">Firman Üyelerde Görünsün</h2>
                <p className="text-sm text-[#7C6E70] mt-1">İşletmenizin reklam banner'ı, çiftlerin (üyelerin) kendi panellerinde gösterilsin. Aşağıdaki formu doldurun, reklam ekibimiz sizinle iletişime geçip banner'ınızı yayınlasın.</p>
              </div>
            </div>

            {!isPremium ? (
              <div className="mt-5 flex items-start gap-3 rounded-xl border border-[#E3CE9E] bg-[#FBF3E4] px-4 py-3" data-testid="uye-ads-premium-lock">
                <Crown className="w-5 h-5 text-[#8a6d1f] shrink-0 mt-0.5" />
                <p className="text-sm text-[#8a6d1f]">Üye paneli reklam talebi yalnızca <strong>premium üyelere</strong> açıktır. Ana Sayfa sekmesinden Premium'a geçerek bu özelliği kullanabilirsiniz.</p>
              </div>
            ) : (
              <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-[#5b4d4f] mb-1.5 block">Aylık Bütçe (₺)</Label>
                  <Input value={memberAdForm.budget} onChange={(e) => setMemberAdForm({ ...memberAdForm, budget: e.target.value })} data-testid="uye-ad-budget" placeholder="ör. 1500" className="border-[#F0DDE0] focus-visible:ring-[#C15B6B]" />
                </div>
                <div className="sm:col-span-2">
                  <Label className="text-[#5b4d4f] mb-1.5 block">Not / Mesaj</Label>
                  <Textarea value={memberAdForm.notes} onChange={(e) => setMemberAdForm({ ...memberAdForm, notes: e.target.value })} data-testid="uye-ad-notes" rows={4} placeholder="Hedef il, reklam görseliniz, kampanyanız veya beklentilerinizi yazın..." className="border-[#F0DDE0] focus-visible:ring-[#C15B6B]" />
                </div>
                <div>
                  <button onClick={submitMemberAd} disabled={memberAdBusy} data-testid="uye-ad-submit" className="flex items-center justify-center gap-2 h-11 px-6 rounded-xl bg-gradient-to-r from-[#C15B6B] to-[#9E2A3B] text-white text-sm font-medium hover:shadow-md transition-all disabled:opacity-70">
                    {memberAdBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Talep Gönder
                  </button>
                </div>
              </div>
            )}
          </div>

          {myMemberAdRequests.length > 0 && (
            <div className="bg-white rounded-2xl border border-[#F0DDE0] overflow-hidden" data-testid="uye-ads-history">
              <div className="px-6 py-4 border-b border-[#F0DDE0]">
                <h3 className="font-medium text-[#2A2224]">Talep Geçmişiniz</h3>
              </div>
              <div className="divide-y divide-[#F0DDE0]">
                {myMemberAdRequests.map((r) => {
                  const st = r.status || "pending";
                  const badge = st === "done"
                    ? { c: "bg-[#E7F6EC] text-[#237045]", t: "İletişime geçildi / Tamamlandı" }
                    : st === "rejected"
                    ? { c: "bg-[#FBEEF0] text-[#9E2A3B]", t: "Reddedildi" }
                    : { c: "bg-[#FBF3E4] text-[#8a6d1f]", t: "Bekliyor" };
                  return (
                    <div key={r.id} className="px-6 py-4 flex flex-col sm:flex-row sm:items-center gap-2" data-testid={`uye-ad-req-${r.id}`}>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          {r.budget && <span className="text-sm text-[#2A2224] font-medium">Bütçe: {r.budget} ₺</span>}
                          <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${badge.c}`}>{badge.t}</span>
                        </div>
                        {r.notes && <p className="text-sm text-[#7C6E70] mt-0.5 truncate">{r.notes}</p>}
                      </div>
                      <span className="text-xs text-[#a8999b] shrink-0">{r.createdAt ? new Date(r.createdAt).toLocaleString("tr-TR") : ""}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
        )}

        {section === "info" && infoTab === "contact" && form && (
        <div className="space-y-6" data-testid="firm-contact-prefs">
          <div className="bg-white rounded-2xl border border-[#F0DDE0] p-6">
            <div className="flex items-center gap-2 mb-1">
              <Phone className="w-5 h-5 text-[#25D366]" />
              <h2 className="font-display text-xl text-[#2A2224]">İletişim Tercihleri</h2>
            </div>
            <p className="text-sm text-[#7C6E70] mb-5">Müşterilerinizin işletme sayfanızdan size nasıl ulaşacağını buradan yönetin.</p>

            <div className="space-y-2 max-w-xl">
              <Label>İşyeri Telefon Numarası</Label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9E2A3B]" />
                <Input
                  data-testid="firm-public-phone-input"
                  value={form.publicPhone || ""}
                  onChange={(e) => set("publicPhone", e.target.value)}
                  placeholder="0212 XXX XX XX"
                  className="pl-9 border-[#F0DDE0] focus-visible:ring-[#C15B6B]"
                />
              </div>
              <p className="text-xs text-[#7C6E70]">İşletme sayfanızdaki "Hemen Ara" butonu bu numarayı arar.</p>
            </div>

            <div className="space-y-2 max-w-xl mt-5">
              <Label>WhatsApp Linki / Numarası</Label>
              <div className="relative">
                <Send className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#25D366]" />
                <Input
                  data-testid="firm-whatsapp-url-input"
                  value={form.whatsappUrl || ""}
                  onChange={(e) => set("whatsappUrl", e.target.value)}
                  onBlur={() => setForm((f) => ({ ...f, whatsappUrl: normalizeWhatsapp(f.whatsappUrl) }))}
                  placeholder="Numara (05XX...) veya https://wa.me/... yapıştırın"
                  className="pl-9 border-[#F0DDE0] focus-visible:ring-[#C15B6B]"
                />
              </div>
              <p className="text-xs text-[#7C6E70]">Numara girerseniz otomatik olarak WhatsApp linkine dönüştürülür. Link girildiğinde işletme sayfanızda "WhatsApp" butonu görünür.</p>
              {form.whatsappUrl ? (
                <a
                  href={normalizeWhatsapp(form.whatsappUrl)}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="firm-whatsapp-active-preview"
                  className="inline-flex items-center gap-2 mt-1 px-3 h-9 rounded-lg bg-[#25D366] text-white text-sm font-medium hover:brightness-95 transition-all"
                >
                  <MessageCircle className="w-4 h-4" /> WhatsApp aktif — Test et
                </a>
              ) : (
                <span data-testid="firm-whatsapp-inactive" className="inline-flex items-center gap-2 mt-1 px-3 h-9 rounded-lg bg-[#F3E9EB] text-[#a8999b] text-sm">
                  <MessageCircle className="w-4 h-4" /> WhatsApp pasif — numara girin
                </span>
              )}
            </div>

            <div className="space-y-2 max-w-xl mt-5">
              <Label>SMS Bildirim Numarası</Label>
              <div className="relative">
                <MessageCircle className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9E2A3B]" />
                <Input
                  data-testid="firm-sms-notify-input"
                  value={form.smsNotifyPhone || ""}
                  onChange={(e) => set("smsNotifyPhone", e.target.value)}
                  placeholder="05XX XXX XX XX"
                  className="pl-9 border-[#F0DDE0] focus-visible:ring-[#C15B6B]"
                />
              </div>
              <p className="text-xs text-[#7C6E70]">Bir üye sizden teklif istediğinde bu numaraya SMS ile bildirim gönderilir. Boş bırakırsanız SMS gönderilmez.</p>
            </div>

            {isPremium ? (
            <>
            <div className="space-y-2 max-w-xl mt-5">
              <Label>Konum (Google Haritalar Linki veya Adres)</Label>
              <div className="relative">
                <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9E2A3B]" />
                <Input data-testid="firm-map-url-input" value={form.mapUrl || ""} onChange={(e) => set("mapUrl", e.target.value)} placeholder="https://maps.google.com/... veya açık adres" className="pl-9 border-[#F0DDE0] focus-visible:ring-[#C15B6B]" />
              </div>
              <p className="text-xs text-[#7C6E70]">İşletme sayfanızda harita ve "Yol Tarifi Al" butonu görünür.</p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-3xl mt-5">
              <div className="space-y-2">
                <Label>Website</Label>
                <div className="relative">
                  <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#7C6E70]" />
                  <Input data-testid="firm-website-input" value={form.website || ""} onChange={(e) => set("website", e.target.value)} onBlur={() => set("website", normalizeUrl(form.website))} placeholder="https://siteniz.com" className="pl-9 border-[#F0DDE0] focus-visible:ring-[#C15B6B]" />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Facebook</Label>
                <div className="relative">
                  <Facebook className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#1877F2]" />
                  <Input data-testid="firm-facebook-input" value={form.facebook || ""} onChange={(e) => set("facebook", e.target.value)} onBlur={() => set("facebook", normalizeSocial(form.facebook, "facebook.com"))} placeholder="https://facebook.com/..." className="pl-9 border-[#F0DDE0] focus-visible:ring-[#C15B6B]" />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Twitter</Label>
                <div className="relative">
                  <Twitter className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#1DA1F2]" />
                  <Input data-testid="firm-twitter-input" value={form.twitter || ""} onChange={(e) => set("twitter", e.target.value)} onBlur={() => set("twitter", normalizeSocial(form.twitter, "twitter.com"))} placeholder="https://twitter.com/..." className="pl-9 border-[#F0DDE0] focus-visible:ring-[#C15B6B]" />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Instagram</Label>
                <div className="relative">
                  <Instagram className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#E1306C]" />
                  <Input data-testid="firm-instagram-input" value={form.instagram || ""} onChange={(e) => set("instagram", e.target.value)} onBlur={() => set("instagram", normalizeSocial(form.instagram, "instagram.com"))} placeholder="https://instagram.com/..." className="pl-9 border-[#F0DDE0] focus-visible:ring-[#C15B6B]" />
                </div>
              </div>
              <div className="space-y-2">
                <Label>YouTube</Label>
                <div className="relative">
                  <Youtube className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#FF0000]" />
                  <Input data-testid="firm-youtube-input" value={form.youtube || ""} onChange={(e) => set("youtube", e.target.value)} onBlur={() => set("youtube", normalizeSocial(form.youtube, "youtube.com"))} placeholder="https://youtube.com/@..." className="pl-9 border-[#F0DDE0] focus-visible:ring-[#C15B6B]" />
                </div>
              </div>
            </div>
            </>
            ) : (
              <div className="mt-5 flex items-start gap-3 rounded-xl border border-[#E3CE9E] bg-[#FBF3E4] px-4 py-3 max-w-3xl" data-testid="contact-premium-lock">
                <Crown className="w-5 h-5 text-[#C6A15B] mt-0.5 shrink-0" />
                <p className="text-sm text-[#8a6d1f]">Konum, Website ve sosyal medya (Facebook, Twitter, Instagram, YouTube) bağlantıları yalnızca <strong>premium üyelere</strong> açıktır. Ana Sayfa sekmesinden Premium'a geçerek bu alanları kullanabilirsiniz.</p>
              </div>
            )}

            <button onClick={handleSave} disabled={saving} data-testid="firm-contact-save-btn" className="mt-6 flex items-center gap-2 px-6 h-11 rounded-xl bg-gradient-to-r from-[#C15B6B] to-[#9E2A3B] text-white text-sm font-medium hover:shadow-md transition-all disabled:opacity-70">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Kaydet
            </button>
          </div>
        </div>
        )}


        {section === "info" && infoTab === "stats" && (
          <StatsPanel stats={firm?.stats} />
        )}

        {section === "info" && infoTab === "yetkili" && form && (
        <div className="space-y-6" data-testid="firm-yetkili-tab">
          <div className="bg-white rounded-2xl border border-[#F0DDE0] p-6">
            <div className="flex items-center gap-2 mb-1">
              <User className="w-5 h-5 text-[#9E2A3B]" />
              <h2 className="font-display text-xl text-[#2A2224]">Yetkili Kişi</h2>
            </div>
            <p className="text-sm text-[#7C6E70] mb-5">İşletmenin yetkili / iletişim kişisi bilgileri.</p>
            <div className="space-y-2 max-w-xl">
              <Label>Yetkili Kişi Adı</Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9E2A3B]" />
                <Input data-testid="firm-contact-name-input" value={form.contactName || ""} onChange={(e) => set("contactName", e.target.value)} placeholder="Örn. Ahmet Yılmaz" className="pl-9 border-[#F0DDE0] focus-visible:ring-[#C15B6B]" />
              </div>
            </div>
            <div className="space-y-2 max-w-xl mt-5">
              <Label>E-posta</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9E2A3B]" />
                <Input data-testid="firm-email-input" value={form.email || ""} onChange={(e) => set("email", e.target.value)} placeholder="ornek@firma.com" className="pl-9 border-[#F0DDE0] focus-visible:ring-[#C15B6B]" />
              </div>
            </div>
            <button onClick={handleSave} disabled={saving} data-testid="firm-yetkili-save-btn" className="mt-6 flex items-center gap-2 px-6 h-11 rounded-xl bg-gradient-to-r from-[#C15B6B] to-[#9E2A3B] text-white text-sm font-medium hover:shadow-md transition-all disabled:opacity-70">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Kaydet
            </button>
          </div>

          {adminMode && (
          <div className="bg-white rounded-2xl border border-[#F0DDE0] p-6" data-testid="firm-password-card">
            <div className="flex items-center gap-2 mb-1">
              <Lock className="w-5 h-5 text-[#9E2A3B]" />
              <h2 className="font-display text-xl text-[#2A2224]">Panel Giriş Şifresi</h2>
            </div>
            <p className="text-sm text-[#7C6E70] mb-5">Firmaya panele giriş şifresi verin. Firma, <strong>giriş telefonu ({firm?.phone || "—"})</strong> + bu şifreyle <span className="whitespace-nowrap">/firma-panel</span> üzerinden giriş yapar. Mevcut şifre bu işlemle değiştirilir.</p>
            <div className="space-y-2 max-w-xl">
              <Label>Yeni Şifre</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9E2A3B]" />
                <Input data-testid="firm-password-input" type="text" value={firmPassword} onChange={(e) => setFirmPassword(e.target.value)} placeholder="En az 4 karakter" className="pl-9 border-[#F0DDE0] focus-visible:ring-[#C15B6B]" />
              </div>
              <p className="text-xs text-[#9c8f90]">Şifreyi firmayla paylaşmayı unutmayın; sistemde şifreli saklanır, sonradan görüntülenemez.</p>
            </div>
            <button onClick={setFirmLoginPassword} disabled={settingPassword || firmPassword.trim().length < 4} data-testid="firm-password-save-btn" className="mt-6 flex items-center gap-2 px-6 h-11 rounded-xl bg-gradient-to-r from-[#C15B6B] to-[#9E2A3B] text-white text-sm font-medium hover:shadow-md transition-all disabled:opacity-60">
              {settingPassword ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />} Şifreyi Belirle
            </button>
          </div>
          )}

          <div className="bg-white rounded-2xl border border-[#F0DDE0] p-6" data-testid="firm-contact-summary">
            <div className="flex items-center gap-2 mb-4">
              <Phone className="w-5 h-5 text-[#25D366]" />
              <h2 className="font-display text-xl text-[#2A2224]">İletişim Bilgileri</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 text-sm">
              {[
                { label: "Giriş Telefonu", value: firm?.phone },
                { label: "Genel Telefon", value: form.publicPhone },
                { label: "WhatsApp", value: form.whatsappUrl },
                { label: "E-posta", value: form.email },
                { label: "Şehir / İlçe", value: [form.district, form.city].filter(Boolean).join(" / ") },
                { label: "Konum", value: form.mapUrl },
                { label: "Website", value: form.website },
                { label: "Instagram", value: form.instagram },
                { label: "Facebook", value: form.facebook },
              ].map((row) => (
                <div key={row.label} className="flex flex-col gap-0.5 border-b border-[#F5E9EB] pb-2">
                  <span className="text-xs text-[#9c8f90]">{row.label}</span>
                  <span className="text-[#2A2224] break-all">{row.value || "—"}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
        )}


        {section === "info" && infoTab === "main" && adminMode && (() => {
          const nowT = Date.now();
          const catName = (slug) => (CATEGORIES.find((c) => c.slug === slug) || {}).name || slug;
          const activeExtras = (firm?.extraCategories || []).filter((e) => e.until && new Date(e.until).getTime() > nowT);
          return (
            <div className="bg-white rounded-2xl border border-[#F0DDE0] p-4 flex flex-wrap items-center gap-2" data-testid="admin-cat-switcher">
              <span className="text-sm text-[#7C6E70] mr-1">Kategori:</span>
              <button onClick={() => setActiveExtraSlug(null)} data-testid="admin-cat-tab-main"
                className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-sm font-medium transition-all ${!activeExtraSlug ? "bg-[#9E2A3B] text-white" : "bg-[#FBF3E4] text-[#8a6d1f] border border-[#E3CE9E] hover:border-[#C6A15B]"}`}>
                {catName(firm?.category)} <span className="opacity-70 text-xs">(ana)</span>
              </button>
              {activeExtras.map((e) => (
                <button key={e.category} onClick={() => setActiveExtraSlug(e.category)} data-testid={`admin-cat-tab-${e.category}`}
                  className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-sm font-medium transition-all ${activeExtraSlug === e.category ? "bg-[#9E2A3B] text-white" : "bg-[#FBF3E4] text-[#8a6d1f] border border-[#E3CE9E] hover:border-[#C6A15B]"}`}>
                  {catName(e.category)}
                </button>
              ))}
            </div>
          );
        })()}

        {section === "info" && infoTab === "main" && !(adminMode && activeExtraSlug) && (
        <div className="space-y-6">
          <div className="bg-white rounded-2xl border border-[#F0DDE0] p-6">
            <div className="flex flex-wrap items-center gap-x-3 gap-y-2 mb-5">
              <h2 className="font-display text-xl text-[#2A2224]">Firma Bilgileri</h2>
              {!adminMode && (() => {
                const nowT = Date.now();
                const catName = (slug) => (CATEGORIES.find((c) => c.slug === slug) || {}).name || slug;
                const activeExtras = (firm?.extraCategories || []).filter((e) => e.until && new Date(e.until).getTime() > nowT);
                return (
                  <div className="flex flex-wrap items-center gap-1.5" data-testid="firm-published-categories">
                    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-[#9E2A3B] text-white text-xs font-medium">
                      {catName(firm?.category)} <span className="opacity-70">(ana)</span>
                    </span>
                    {activeExtras.map((e) => (
                      <span key={e.category} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-[#FBF3E4] text-[#8a6d1f] text-xs font-medium border border-[#E3CE9E]">
                        {catName(e.category)}
                      </span>
                    ))}
                  </div>
                );
              })()}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1.5 md:col-span-2">
                <Label>Firma / Salon Adı</Label>
                <div className="relative">
                  <Building2 className="w-4 h-4 text-[#C15B6B] absolute left-3 top-1/2 -translate-y-1/2" />
                  <Input value={form.name} onChange={(e) => set("name", e.target.value)} className="pl-9 border-[#F0DDE0] focus-visible:ring-[#C15B6B]" />
                </div>
              </div>

              <div className="space-y-1.5">
                <Label>Kategori</Label>
                <Select value={form.category} onValueChange={(v) => set("category", v)}>
                  <SelectTrigger className="border-[#F0DDE0] focus:ring-[#C15B6B]"><SelectValue /></SelectTrigger>
                  <SelectContent className="max-h-60">
                    {CATEGORIES.map((c) => <SelectItem key={c.slug} value={c.slug}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1.5">
                <Label>Şehir</Label>
                <Select value={form.city} onValueChange={(v) => setForm((f) => ({ ...f, city: v, district: "" }))}>
                  <SelectTrigger data-testid="firm-edit-city" className="border-[#F0DDE0] focus:ring-[#C15B6B]"><SelectValue /></SelectTrigger>
                  <SelectContent className="max-h-60">
                    {CITIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1.5">
                <Label>İlçe</Label>
                <div className="relative">
                  <MapPin className="w-4 h-4 text-[#C15B6B] absolute left-3 top-1/2 -translate-y-1/2 z-10" />
                  <Select value={form.district} onValueChange={(v) => set("district", v)} disabled={!form.city}>
                    <SelectTrigger data-testid="firm-edit-district" className="pl-9 border-[#F0DDE0] focus:ring-[#C15B6B]"><SelectValue placeholder="İlçe seçin" /></SelectTrigger>
                    <SelectContent className="max-h-60">
                      {(form.district && !districtsOf(form.city).includes(form.district) ? [form.district, ...districtsOf(form.city)] : districtsOf(form.city)).map((d) => (
                        <SelectItem key={d} value={d}>{d}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {firmGroup === "mekan" && <div className="space-y-1.5">
                <Label>Kapasite</Label>
                <div className="relative">
                  <Users className="w-4 h-4 text-[#C15B6B] absolute left-3 top-1/2 -translate-y-1/2" />
                  <Input value={form.capacity} onChange={(e) => set("capacity", e.target.value)} placeholder="Örn. 300 - 700 Kişi" className="pl-9 border-[#F0DDE0] focus-visible:ring-[#C15B6B]" />
                </div>
              </div>}

              <div className="space-y-1.5 md:col-span-2">
                <Label>Başlangıç Fiyatı (₺)</Label>
                <div className="relative">
                  <Tag className="w-4 h-4 text-[#C15B6B] absolute left-3 top-1/2 -translate-y-1/2" />
                  <Input type="number" value={form.priceFrom} onChange={(e) => set("priceFrom", e.target.value)} placeholder="Örn. 45000" className="pl-9 border-[#F0DDE0] focus-visible:ring-[#C15B6B]" />
                </div>
                <div className="flex flex-wrap gap-2 pt-1" data-testid="price-type-group">
                  {[["", "Otomatik"], ["kisi", "Kişi başı"], ["toplam", "Toplam"]].map(([v, l]) => (
                    <button key={v || "auto"} type="button" onClick={() => set("priceType", v)} data-testid={`price-type-${v || "auto"}`}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${form.priceType === v ? "bg-[#9E2A3B] text-white border-[#9E2A3B]" : "bg-white text-[#7C6E70] border-[#F0DDE0] hover:border-[#C15B6B]"}`}>{l}</button>
                  ))}
                </div>
                <p className="text-xs text-[#a8999b]">Otomatik: mekanlarda 5.000 ₺ altı fiyatlar "kişi başı" gösterilir.</p>
              </div>

              <div className="space-y-1.5 md:col-span-2">
                <Label>Hakkımızda</Label>
                <Textarea value={form.description} onChange={(e) => set("description", e.target.value)} rows={4} placeholder="Salonunuzu, hizmetlerinizi ve öne çıkan özelliklerinizi anlatın..." className="border-[#F0DDE0] focus-visible:ring-[#C15B6B]" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-[#F0DDE0] p-6">
            <h2 className="font-display text-xl text-[#2A2224] mb-1">Özellikler / Hizmetler</h2>
            <p className="text-sm text-[#7C6E70] mb-4">Özellikleri ve sunduğunuz hizmetleri seçiniz.{isPremium ? " Listede yoksa kendiniz ekleyebilirsiniz." : ""}</p>
            <div className="flex flex-wrap gap-2">
              {[...featureOptions, ...form.features.filter((f) => !featureOptions.includes(f))].map((f) => {
                const active = form.features.includes(f);
                return (
                  <button
                    key={f}
                    type="button"
                    data-testid={`feature-chip-${f}`}
                    onClick={() => toggleFeature(f)}
                    className={`px-3.5 py-1.5 rounded-full text-sm border transition-all ${active ? "bg-[#9E2A3B] text-white border-[#9E2A3B]" : "bg-white text-[#5b4d4f] border-[#F0DDE0] hover:border-[#E7A9B0]"}`}
                  >
                    {f}
                  </button>
                );
              })}
            </div>
            {isPremium ? (
            <>
            <div className="flex items-center gap-2 mt-4 max-w-md">
              <Input
                data-testid="custom-feature-input"
                value={customFeature}
                onChange={(e) => setCustomFeature(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addCustomFeature(); } }}
                placeholder="Kendi özelliğinizi yazın (örn. Havuz Başı)"
                className="border-[#F0DDE0] focus-visible:ring-[#C15B6B]"
              />
              <button
                type="button"
                data-testid="add-custom-feature-btn"
                onClick={addCustomFeature}
                className="shrink-0 flex items-center gap-1.5 px-4 h-10 rounded-xl bg-[#9E2A3B] text-white text-sm font-medium hover:bg-[#8a2333] transition-colors"
              >
                <Check className="w-4 h-4" /> Ekle
              </button>
            </div>
            <p className="text-xs text-[#9c8f90] mt-2 max-w-md">Listede olmayan yeni bir özellik eklerseniz, patron onayından sonra yayınlanacaktır.</p>
            </>
            ) : (
              <div className="mt-4 flex items-start gap-3 rounded-xl border border-[#E3CE9E] bg-[#FBF3E4] px-4 py-3 max-w-md" data-testid="custom-feature-premium-lock">
                <Crown className="w-5 h-5 text-[#C6A15B] mt-0.5 shrink-0" />
                <p className="text-sm text-[#8a6d1f]">Kendi özel özelliğinizi eklemek yalnızca <strong>premium üyelere</strong> açıktır. Ana Sayfa sekmesinden Premium'a geçebilirsiniz.</p>
              </div>
            )}
          </div>
        </div>
        )}

        {section === "payments" && (
        <div className="space-y-6" data-testid="firm-payments-section">
          <div className="bg-white rounded-2xl border border-[#F0DDE0] p-6">
            <div className="flex items-start gap-3 mb-1">
              <CreditCard className="w-5 h-5 text-[#C6A15B] mt-0.5" />
              <div>
                <h2 className="font-display text-xl text-[#2A2224]">Ödemeler</h2>
                <p className="text-sm text-[#7C6E70]">Yaptığınız tüm ödemeler burada listelenir.</p>
              </div>
            </div>
            {payments.length === 0 ? (
              <p className="text-center text-sm text-[#a8999b] py-8">Henüz ödeme yapmadınız.</p>
            ) : (
              <div className="mt-4 space-y-2" data-testid="payment-list">
                {payments.map((p) => (
                  <div key={p.orderId} data-testid={`payment-row-${p.orderId}`} className="flex items-center justify-between gap-3 rounded-xl border border-[#F0DDE0] px-4 py-3">
                    <div className="min-w-0">
                      <span className={`inline-block text-xs font-medium px-2.5 py-1 rounded-full ${p.type === "category" ? "bg-[#FBF3E4] text-[#8a6d1f]" : "bg-[#FBEEF0] text-[#9E2A3B]"}`}>
                        {p.typeLabel}{p.categoryName ? `: ${p.categoryName}` : ""}
                      </span>
                      <p className="text-xs text-[#7C6E70] mt-1.5">{p.paidAt ? new Date(p.paidAt).toLocaleString("tr-TR") : "—"}{p.paymentId ? ` · #${p.paymentId}` : ""}</p>
                    </div>
                    <span className="font-display text-lg font-bold text-[#9E2A3B] shrink-0">{fmtPrice(Number(p.amount))}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        )}

        {section === "admin-ads" && adminMode && (
        <div className="bg-white rounded-2xl border border-[#F0DDE0] p-6" data-testid="admin-firm-ads">
          <div className="flex items-start gap-3 mb-4">
            <Megaphone className="w-5 h-5 text-[#3b5bdb] mt-0.5" />
            <div>
              <h2 className="font-display text-xl text-[#2A2224]">Google Reklam Talepleri</h2>
              <p className="text-sm text-[#7C6E70]">Bu işletmenin gönderdiği Google reklam talepleri ve durumları.</p>
            </div>
          </div>
          {adminAdReqs.length === 0 ? (
            <p className="text-sm text-[#7C6E70] py-8 text-center">Bu işletmeden henüz reklam talebi yok.</p>
          ) : (
            <div className="space-y-3">
              {adminAdReqs.map((r) => {
                const st = r.status || "pending";
                const badge = st === "done"
                  ? { c: "bg-[#E7F6EC] text-[#237045] border-[#B9E3C6]", t: "İletişime geçildi / Tamamlandı" }
                  : st === "rejected"
                  ? { c: "bg-[#FBEEF0] text-[#9E2A3B] border-[#E7A9B0]", t: "Reddedildi" }
                  : { c: "bg-[#FBF3E4] text-[#8a6d1f] border-[#E3CE9E]", t: "Bekliyor" };
                return (
                  <div key={r.id} data-testid={`admin-firm-ad-row-${r.id}`} className="rounded-2xl border border-[#F0DDE0] p-4 flex flex-col sm:flex-row sm:items-center gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`text-xs font-medium px-2 py-0.5 rounded-full border ${badge.c}`}>{badge.t}</span>
                        <span className="text-xs text-[#a8999b]">{r.createdAt ? new Date(r.createdAt).toLocaleString("tr-TR") : ""}</span>
                      </div>
                      <p className="text-sm text-[#5b4d4f] mt-1">{r.budget ? <span className="font-medium">Aylık bütçe: {r.budget} ₺</span> : <span className="text-[#a8999b]">Bütçe belirtilmedi</span>}</p>
                      {r.notes && <p className="text-sm text-[#7C6E70] mt-1">{r.notes}</p>}
                    </div>
                    {st === "pending" && (
                      <div className="flex items-center gap-2 shrink-0">
                        <button onClick={() => markAdminAdReq(r.id, "done")} data-testid={`admin-firm-ad-done-${r.id}`}
                          className="flex items-center gap-1.5 px-4 h-10 rounded-xl bg-gradient-to-r from-[#2E8B57] to-[#237045] text-white text-sm font-medium hover:shadow-md transition-all">
                          <CheckCircle2 className="w-4 h-4" /> Tamamlandı
                        </button>
                        <button onClick={() => markAdminAdReq(r.id, "rejected")} data-testid={`admin-firm-ad-reject-${r.id}`}
                          className="flex items-center gap-1.5 px-4 h-10 rounded-xl border border-[#E7A9B0] text-[#9E2A3B] text-sm font-medium hover:bg-[#FBF0F2] transition-colors">
                          <X className="w-4 h-4" /> Reddet
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
        )}

        {section === "admin-notes" && adminMode && (
        <div className="bg-white rounded-2xl border border-[#F0DDE0] p-6" data-testid="admin-firm-notes">
          <div className="flex items-start gap-3 mb-1">
            <StickyNote className="w-5 h-5 text-[#C6A15B] mt-0.5" />
            <div>
              <h2 className="font-display text-xl text-[#2A2224]">Notlar</h2>
              <p className="text-sm text-[#7C6E70]">Bu işletmeyle ilgili özel notlarınız (sadece yöneticiler görür).</p>
            </div>
          </div>
          <div className="flex flex-col gap-2 my-4">
            <Textarea value={newNote} onChange={(e) => setNewNote(e.target.value)} data-testid="admin-firm-note-input" rows={3} placeholder="İşletmeyle ilgili not yazın..." className="border-[#F0DDE0] focus-visible:ring-[#C15B6B]" />
            <button onClick={addAdminNote} disabled={notesBusy || !newNote.trim()} data-testid="admin-firm-note-add-btn" className="self-end flex items-center gap-2 px-4 h-10 rounded-xl bg-gradient-to-r from-[#C15B6B] to-[#9E2A3B] text-white text-sm font-medium hover:shadow-md transition-all disabled:opacity-70">
              {notesBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <StickyNote className="w-4 h-4" />} Not Ekle
            </button>
          </div>
          {adminNotes.length === 0 ? (
            <p className="text-center text-sm text-[#7C6E70]">Henüz not eklenmedi.</p>
          ) : (
            <div className="space-y-2">
              {adminNotes.map((n) => (
                <div key={n.id} data-testid={`admin-firm-note-${n.id}`} className="rounded-xl border border-[#F0DDE0] bg-[#FBF5F0] px-3 py-2.5">
                  <div className="flex items-start justify-between gap-2">
                    <p className="text-sm text-[#2A2224] whitespace-pre-wrap flex-1">{n.text}</p>
                    <button onClick={() => deleteAdminNote(n.id)} title="Sil" className="text-[#c0392b] hover:bg-[#FBE9EA] rounded p-1 shrink-0"><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                  <p className="text-[11px] text-[#a8999b] mt-1">{n.author} · {n.at ? new Date(n.at).toLocaleString("tr-TR") : ""}</p>
                </div>
              ))}
            </div>
          )}
        </div>
        )}

        {section === "settings" && (
        <div className="space-y-5">
          {settingsTab === "contact" && (
          <>
          <div className="bg-white rounded-2xl border border-[#F0DDE0] p-6">
            <h2 className="font-display text-xl text-[#2A2224] mb-5">Firma Yetkilisi</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>Yetkili Adı</Label>
                <div className="relative">
                  <User className="w-4 h-4 text-[#C15B6B] absolute left-3 top-1/2 -translate-y-1/2" />
                  <Input value={form.contactName} onChange={(e) => set("contactName", e.target.value)} className="pl-9 border-[#F0DDE0] focus-visible:ring-[#C15B6B]" />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label>İletişim Telefonu</Label>
                <div className="relative">
                  <Phone className="w-4 h-4 text-[#C15B6B] absolute left-3 top-1/2 -translate-y-1/2" />
                  <Input value={form.publicPhone} onChange={(e) => set("publicPhone", e.target.value)} className="pl-9 border-[#F0DDE0] focus-visible:ring-[#C15B6B]" />
                </div>
              </div>
              <div className="space-y-1.5 md:col-span-2">
                <Label>E-posta</Label>
                <div className="relative">
                  <Mail className="w-4 h-4 text-[#C15B6B] absolute left-3 top-1/2 -translate-y-1/2" />
                  <Input value={form.email} onChange={(e) => set("email", e.target.value)} className="pl-9 border-[#F0DDE0] focus-visible:ring-[#C15B6B]" />
                </div>
              </div>
            </div>
          </div>

          <button
            onClick={handleSave}
            disabled={saving}
            data-testid="firm-save-settings-button"
            className="w-full flex items-center justify-center gap-2 h-12 rounded-xl bg-gradient-to-r from-[#C15B6B] to-[#9E2A3B] text-white font-medium shadow-md hover:shadow-lg hover:-translate-y-0.5 transition-all disabled:opacity-70"
          >
            {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />}
            {saving ? "Kaydediliyor..." : "Firma Yetkilisi Bilgilerini Kaydet"}
          </button>
          </>
          )}

          {settingsTab === "users" && (
          <div className="space-y-5" data-testid="firm-users-section">
          <div className="bg-white rounded-2xl border border-[#F0DDE0] p-6">
            <div className="flex items-start gap-3 mb-1">
              <Users className="w-5 h-5 text-[#C6A15B] mt-0.5" />
              <div>
                <h2 className="font-display text-xl text-[#2A2224]">Kullanıcılar</h2>
                <p className="text-sm text-[#7C6E70]">Panele giriş yapabilecek ek kullanıcılar (personel) ekleyin. Her kullanıcı kendi telefonu ve şifresiyle giriş yapar, tüm yetkilere sahiptir.</p>
              </div>
            </div>

            <div className="mt-5 grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="space-y-1.5">
                <Label>Ad Soyad</Label>
                <div className="relative">
                  <User className="w-4 h-4 text-[#C15B6B] absolute left-3 top-1/2 -translate-y-1/2" />
                  <Input data-testid="new-user-name" value={newUser.name} onChange={(e) => setNewUser((p) => ({ ...p, name: e.target.value }))} placeholder="Personel adı" className="pl-9 border-[#F0DDE0] focus-visible:ring-[#C15B6B]" />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label>Telefon</Label>
                <div className="relative">
                  <Phone className="w-4 h-4 text-[#C15B6B] absolute left-3 top-1/2 -translate-y-1/2" />
                  <Input data-testid="new-user-phone" value={newUser.phone} onChange={(e) => setNewUser((p) => ({ ...p, phone: e.target.value }))} placeholder="05XX XXX XX XX" className="pl-9 border-[#F0DDE0] focus-visible:ring-[#C15B6B]" />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label>Şifre</Label>
                <div className="relative">
                  <Lock className="w-4 h-4 text-[#C15B6B] absolute left-3 top-1/2 -translate-y-1/2" />
                  <Input data-testid="new-user-password" type="password" value={newUser.password} onChange={(e) => setNewUser((p) => ({ ...p, password: e.target.value }))} placeholder="En az 4 karakter" className="pl-9 border-[#F0DDE0] focus-visible:ring-[#C15B6B]" />
                </div>
              </div>
            </div>
            <button
              onClick={addSubUser}
              disabled={userBusy}
              data-testid="add-user-button"
              className="mt-4 flex items-center gap-2 px-5 h-11 rounded-xl bg-gradient-to-r from-[#C15B6B] to-[#9E2A3B] text-white text-sm font-medium hover:shadow-md transition-all disabled:opacity-70"
            >
              {userBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />} Kullanıcı Ekle
            </button>
          </div>

          <div className="bg-white rounded-2xl border border-[#F0DDE0] p-6">
            <h3 className="font-display text-lg text-[#2A2224] mb-4">Kayıtlı Kullanıcılar</h3>
            {subUsers.length === 0 ? (
              <p className="text-sm text-[#a8999b]">Henüz ek kullanıcı eklemediniz.</p>
            ) : (
              <div className="space-y-2" data-testid="user-list">
                {subUsers.map((u) => (
                  <div key={u.id} data-testid={`user-row-${u.id}`} className="flex items-center justify-between gap-3 rounded-xl border border-[#F0DDE0] px-4 py-3">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-9 h-9 rounded-full bg-[#FBF0F2] flex items-center justify-center shrink-0">
                        <User className="w-4 h-4 text-[#9E2A3B]" />
                      </div>
                      <div className="min-w-0">
                        <p className="font-medium text-[#2A2224] truncate">{u.name}</p>
                        <p className="text-sm text-[#7C6E70]">{u.phone}</p>
                      </div>
                    </div>
                    <button
                      onClick={() => deleteSubUser(u.id)}
                      data-testid={`delete-user-${u.id}`}
                      className="flex items-center gap-1.5 px-3 h-9 rounded-lg border border-[#E7A9B0] text-[#9E2A3B] text-sm font-medium hover:bg-[#FBF0F2] transition-colors shrink-0"
                    >
                      <Trash2 className="w-3.5 h-3.5" /> Sil
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
          </div>
          )}
        </div>
        )}

        {section === "info" && infoTab === "main" && !(adminMode && activeExtraSlug) && (
        <div>
          <div className="bg-white rounded-2xl border border-[#F0DDE0] p-6">
            <div className="flex items-center justify-between mb-1">
              <h2 className="font-display text-xl text-[#2A2224]">Salon Fotoğrafları</h2>
              <span data-testid="photo-count" className={`text-sm font-medium px-3 py-1 rounded-full ${realImages.length >= imgLimit ? "bg-[#FBEEF0] text-[#9E2A3B]" : "bg-[#F3EDEE] text-[#7C6E70]"}`}>{realImages.length} / {imgLimit}</span>
            </div>
            <p className="text-sm text-[#7C6E70] mb-4">İlk fotoğraf vitrin (kapak) görseli olarak kullanılır.{!isPremium && ` Ücretsiz üyelikte en fazla ${imgLimit} fotoğraf ekleyebilirsiniz.`}</p>

            <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={handleFiles} />
            <button
              onClick={() => fileRef.current?.click()}
              disabled={uploading || realImages.length >= imgLimit}
              data-testid="photo-add-button"
              className="w-full flex flex-col items-center justify-center gap-2 py-8 rounded-xl border-2 border-dashed border-[#E7A9B0] text-[#9E2A3B] hover:bg-[#FBF0F2] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {uploading ? <Loader2 className="w-7 h-7 animate-spin" /> : <ImagePlus className="w-7 h-7" />}
              <span className="text-sm font-medium">{uploading ? "Yükleniyor..." : realImages.length >= imgLimit ? "Limit doldu" : "Fotoğraf Ekle"}</span>
              <span className="text-xs text-[#7C6E70]">JPG / PNG • en fazla {imgLimit} adet</span>
            </button>

            {realImages.length >= imgLimit && !isPremium && (
              <div className="mt-3 flex items-center gap-2 rounded-xl border border-[#E3CE9E] bg-[#FBF3E4] px-4 py-3">
                <Lock className="w-4 h-4 text-[#a9843f] shrink-0" />
                <p className="text-sm text-[#8a6d1f]">Fotoğraf limitiniz doldu. Daha fazla fotoğraf için "Ana Sayfa" sekmesinden Premium'a geçin.</p>
              </div>
            )}

            {realImages.length > 0 && (
              <SortableGallery images={realImages} onDelete={removeImage} onReorder={(order) => reorderImages(order)} captions={firm?.captions} onCaption={saveCaption} testid="firm-gallery" />
            )}

            {realImages.length === 0 && (
              <p className="text-center text-sm text-[#7C6E70] mt-4">Henüz fotoğraf eklemediniz.</p>
            )}
          </div>
        </div>
        )}

        {section === "info" && infoTab === "main" && !(adminMode && activeExtraSlug) && (
          <button
            onClick={handleSave}
            disabled={saving}
            data-testid="firm-save-info-button"
            className="w-full flex items-center justify-center gap-2 h-12 rounded-xl bg-gradient-to-r from-[#C15B6B] to-[#9E2A3B] text-white font-medium shadow-md hover:shadow-lg hover:-translate-y-0.5 transition-all disabled:opacity-70"
          >
            {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />}
            {saving ? "Kaydediliyor..." : "Bilgileri Kaydet ve Yayınla"}
          </button>
        )}

        {section === "info" && infoTab === "main" && adminMode && activeExtraSlug && (() => {
          const e = (firm?.extraCategories || []).find((x) => x.category === activeExtraSlug);
          if (!e) return null;
          const catName = (slug) => (CATEGORIES.find((c) => c.slug === slug) || {}).name || slug;
          const imgs = e.images || [];
          return (
            <div data-testid={`admin-extra-editor-${e.category}`} className="space-y-6">
              <div className="bg-white rounded-2xl border border-[#F0DDE0] p-6 space-y-3">
                <h3 className="font-medium text-[#2A2224] flex items-center gap-2"><Sparkles className="w-4 h-4 text-[#C6A15B]" /> {catName(e.category)} — Bu Kategori İçin Bilgiler</h3>
                <p className="text-xs text-[#7C6E70]">Boş bırakılan alanlar ana işletme bilgilerinden alınır. Bu kategoride ayrı isim, açıklama ve fotoğraflar gösterebilirsiniz.</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <label className="text-sm text-[#5b4d4f]">İşletme Adı</label>
                    <Input data-testid={`admin-extra-name-${e.category}`} value={extraVal(e.category, "name")} onChange={(ev) => setExtraVal(e.category, "name", ev.target.value)} placeholder={firm?.name} className="border-[#F0DDE0] focus-visible:ring-[#C15B6B]" />
                  </div>
                  {(CATEGORIES.find((c) => c.slug === e.category) || {}).group === "mekan" && (
                  <div className="space-y-1.5">
                    <label className="text-sm text-[#5b4d4f]">Kapasite</label>
                    <Input value={extraVal(e.category, "capacity")} onChange={(ev) => setExtraVal(e.category, "capacity", ev.target.value)} className="border-[#F0DDE0] focus-visible:ring-[#C15B6B]" />
                  </div>
                  )}
                  <div className="space-y-1.5">
                    <label className="text-sm text-[#5b4d4f]">Başlangıç Fiyatı (₺)</label>
                    <Input type="number" value={extraVal(e.category, "priceFrom")} onChange={(ev) => setExtraVal(e.category, "priceFrom", ev.target.value)} className="border-[#F0DDE0] focus-visible:ring-[#C15B6B]" />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-sm text-[#5b4d4f]">İletişim Telefonu</label>
                    <Input value={extraVal(e.category, "publicPhone")} onChange={(ev) => setExtraVal(e.category, "publicPhone", ev.target.value)} className="border-[#F0DDE0] focus-visible:ring-[#C15B6B]" />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <label className="text-sm text-[#5b4d4f]">Hakkımızda</label>
                  <Textarea value={extraVal(e.category, "description")} onChange={(ev) => setExtraVal(e.category, "description", ev.target.value)} rows={3} placeholder={firm?.description} className="border-[#F0DDE0] focus-visible:ring-[#C15B6B]" />
                </div>
              </div>

              <div className="bg-white rounded-2xl border border-[#F0DDE0] p-6 space-y-2">
                <h3 className="font-display text-xl text-[#2A2224]">Özellikler / Hizmetler</h3>
                <p className="text-sm text-[#7C6E70]">Özellikleri ve sunduğunuz hizmetleri seçiniz. Listede yoksa kendiniz ekleyebilirsiniz.</p>
                <div className="flex flex-wrap gap-2" data-testid={`admin-extra-features-${e.category}`}>
                  {[...optsFor(e.category), ...extraFeatures(e.category).filter((f) => !optsFor(e.category).includes(f))].map((f) => {
                    const active = extraFeatures(e.category).includes(f);
                    return (
                      <button key={f} type="button" data-testid={`admin-extra-feature-chip-${e.category}-${f}`} onClick={() => toggleExtraFeature(e.category, f)}
                        className={`px-3 py-1.5 rounded-full text-sm border transition-all ${active ? "bg-[#9E2A3B] text-white border-[#9E2A3B]" : "bg-white text-[#5b4d4f] border-[#F0DDE0] hover:border-[#E7A9B0]"}`}>
                        {f}
                      </button>
                    );
                  })}
                </div>
                <div className="flex items-center gap-2 mt-2 max-w-md">
                  <Input data-testid={`admin-extra-custom-feature-input-${e.category}`} value={extraCustomFeature[e.category] || ""}
                    onChange={(ev) => setExtraCustomFeature((p) => ({ ...p, [e.category]: ev.target.value }))}
                    onKeyDown={(ev) => { if (ev.key === "Enter") { ev.preventDefault(); addExtraCustomFeature(e.category); } }}
                    placeholder="Kendi özelliğinizi yazın (örn. Havuz Başı)" className="border-[#F0DDE0] focus-visible:ring-[#C15B6B]" />
                  <button type="button" data-testid={`admin-extra-add-custom-feature-btn-${e.category}`} onClick={() => addExtraCustomFeature(e.category)}
                    className="shrink-0 flex items-center gap-1.5 px-4 h-10 rounded-xl bg-[#9E2A3B] text-white text-sm font-medium hover:bg-[#8a2333] transition-colors">
                    <Check className="w-4 h-4" /> Ekle
                  </button>
                </div>
              </div>

              <div className="bg-white rounded-2xl border border-[#F0DDE0] p-6">
                <div className="flex items-center justify-between mb-1">
                  <h3 className="font-display text-xl text-[#2A2224]">Salon Fotoğrafları</h3>
                  <span className={`text-sm font-medium px-3 py-1 rounded-full ${imgs.length >= imgLimit ? "bg-[#FBEEF0] text-[#9E2A3B]" : "bg-[#F3EDEE] text-[#7C6E70]"}`}>{imgs.length} / {imgLimit}</span>
                </div>
                <p className="text-sm text-[#7C6E70] mb-4">İlk fotoğraf vitrin (kapak) görseli olarak kullanılır.</p>
                <input ref={(el) => (extraFileRefs.current[e.category] = el)} type="file" accept="image/*" multiple className="hidden" onChange={(ev) => handleExtraFiles(e.category, ev)} />
                <button onClick={() => extraFileRefs.current[e.category]?.click()} disabled={extraUploading === e.category || imgs.length >= imgLimit} data-testid={`admin-extra-photo-add-${e.category}`}
                  className="w-full flex flex-col items-center justify-center gap-2 py-8 rounded-xl border-2 border-dashed border-[#E7A9B0] text-[#9E2A3B] hover:bg-[#FBF0F2] transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                  {extraUploading === e.category ? <Loader2 className="w-7 h-7 animate-spin" /> : <ImagePlus className="w-7 h-7" />}
                  <span className="text-sm font-medium">{extraUploading === e.category ? "Yükleniyor..." : imgs.length >= imgLimit ? "Limit doldu" : "Fotoğraf Ekle"}</span>
                  <span className="text-xs text-[#7C6E70]">JPG / PNG • en fazla {imgLimit} adet</span>
                </button>
                {imgs.length > 0 && (
                  <SortableGallery images={imgs} onDelete={async (i) => { const r = await axios.delete(`${API}${meBase}/extra-categories/${e.category}/images/${i}`, { headers }); setFirm(r.data); }} onReorder={(order) => reorderImages(order, e.category)} captions={firm?.captions} onCaption={saveCaption} testid={`admin-extra-gallery-${e.category}`} />
                )}
                {imgs.length === 0 && <p className="text-center text-sm text-[#7C6E70] mt-4">Henüz fotoğraf eklemediniz.</p>}
              </div>

              <button onClick={() => saveExtra(e.category)} disabled={extraSaving === e.category} data-testid={`admin-extra-save-${e.category}`}
                className="w-full flex items-center justify-center gap-2 h-12 rounded-xl bg-gradient-to-r from-[#C15B6B] to-[#9E2A3B] text-white font-medium shadow-md hover:shadow-lg hover:-translate-y-0.5 transition-all disabled:opacity-70">
                {extraSaving === e.category ? <Loader2 className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />}
                {extraSaving === e.category ? "Kaydediliyor..." : "Bilgileri Kaydet ve Yayınla"}
              </button>
            </div>
          );
        })()}

        {section === "info" && infoTab === "main" && !adminMode && (
        <div className="bg-white rounded-2xl border border-[#F0DDE0] p-6" data-testid="extra-categories-card">
          <div className="flex items-start gap-3 mb-1">
            <Tag className="w-5 h-5 text-[#C6A15B] mt-0.5" />
            <div>
              <h2 className="font-display text-xl text-[#2A2224]">Ek Kategoriler</h2>
              <p className="text-sm text-[#7C6E70]">İşletmeniz ana kategorisinin yanı sıra ödeme yaparak başka kategorilerde de listelenebilir.</p>
            </div>
          </div>

          {(() => {
            const now = Date.now();
            const activeExtras = (firm?.extraCategories || []).filter((e) => e.until && new Date(e.until).getTime() > now);
            const catName = (slug) => (CATEGORIES.find((c) => c.slug === slug) || {}).name || slug;
            const available = CATEGORIES.filter((c) => c.slug !== firm?.category && !activeExtras.some((e) => e.category === c.slug));
            const tiers = [Number(membership.extraCategoryPrice) || 0, Number(membership.extraCategoryPrice2) || 0, Number(membership.extraCategoryPrice3) || 0];
            const nextSlot = activeExtras.length;
            const maxReached = nextSlot >= 3;
            const nextPrice = maxReached ? 0 : (tiers[nextSlot] || tiers[0]);
            return (
              <div className="mt-4 space-y-4">
                <div className="flex flex-wrap gap-2">
                  <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#9E2A3B] text-white text-sm font-medium">
                    <Check className="w-3.5 h-3.5" /> {catName(firm?.category)} <span className="opacity-70 text-xs">(ana)</span>
                  </span>
                  {activeExtras.map((e) => (
                    <span key={e.category} data-testid={`extra-cat-${e.category}`} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#FBF3E4] text-[#8a6d1f] text-sm font-medium border border-[#E3CE9E]">
                      <Sparkles className="w-3.5 h-3.5 text-[#C6A15B]" /> {catName(e.category)}
                      <span className="text-xs opacity-80">· {new Date(e.until).toLocaleDateString("tr-TR")}</span>
                    </span>
                  ))}
                </div>

                {activeExtras.map((e) => {
                  const imgs = e.images || [];
                  return (
                    <div key={`ed-${e.category}`} data-testid={`extra-editor-${e.category}`} className="space-y-6">
                      <div className="bg-white rounded-2xl border border-[#F0DDE0] p-6 space-y-3">
                        <div className="flex items-start justify-between gap-3">
                          <h3 className="font-medium text-[#2A2224] flex items-center gap-2"><Sparkles className="w-4 h-4 text-[#C6A15B]" /> {catName(e.category)} — Bu Kategori İçin Bilgiler</h3>
                        </div>
                        <p className="text-xs text-[#7C6E70]">Boş bırakılan alanlar ana işletme bilgilerinizden alınır. Bu kategoride ayrı isim, açıklama ve fotoğraflar gösterebilirsiniz.</p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <div className="space-y-1.5">
                            <label className="text-sm text-[#5b4d4f]">İşletme Adı</label>
                            <Input value={extraVal(e.category, "name")} onChange={(ev) => setExtraVal(e.category, "name", ev.target.value)} placeholder={firm?.name} className="border-[#F0DDE0] focus-visible:ring-[#C15B6B]" />
                          </div>
                          {(CATEGORIES.find((c) => c.slug === e.category) || {}).group === "mekan" && (
                          <div className="space-y-1.5">
                            <label className="text-sm text-[#5b4d4f]">Kapasite</label>
                            <Input value={extraVal(e.category, "capacity")} onChange={(ev) => setExtraVal(e.category, "capacity", ev.target.value)} className="border-[#F0DDE0] focus-visible:ring-[#C15B6B]" />
                          </div>
                          )}
                          <div className="space-y-1.5">
                            <label className="text-sm text-[#5b4d4f]">Başlangıç Fiyatı (₺)</label>
                            <Input type="number" value={extraVal(e.category, "priceFrom")} onChange={(ev) => setExtraVal(e.category, "priceFrom", ev.target.value)} className="border-[#F0DDE0] focus-visible:ring-[#C15B6B]" />
                          </div>
                          <div className="space-y-1.5">
                            <label className="text-sm text-[#5b4d4f]">İletişim Telefonu</label>
                            <Input value={extraVal(e.category, "publicPhone")} onChange={(ev) => setExtraVal(e.category, "publicPhone", ev.target.value)} className="border-[#F0DDE0] focus-visible:ring-[#C15B6B]" />
                          </div>
                        </div>
                        <div className="space-y-1.5">
                          <label className="text-sm text-[#5b4d4f]">Hakkımızda</label>
                          <Textarea value={extraVal(e.category, "description")} onChange={(ev) => setExtraVal(e.category, "description", ev.target.value)} rows={3} placeholder={firm?.description} className="border-[#F0DDE0] focus-visible:ring-[#C15B6B]" />
                        </div>
                      </div>

                      <div className="bg-white rounded-2xl border border-[#F0DDE0] p-6 space-y-2">
                        <h3 className="font-display text-xl text-[#2A2224]">Özellikler / Hizmetler</h3>
                        <p className="text-sm text-[#7C6E70]">Özellikleri ve sunduğunuz hizmetleri seçiniz. Listede yoksa kendiniz ekleyebilirsiniz.</p>
                        <div className="flex flex-wrap gap-2" data-testid={`extra-features-${e.category}`}>
                          {[...optsFor(e.category), ...extraFeatures(e.category).filter((f) => !optsFor(e.category).includes(f))].map((f) => {
                            const active = extraFeatures(e.category).includes(f);
                            return (
                              <button
                                key={f}
                                type="button"
                                data-testid={`extra-feature-chip-${e.category}-${f}`}
                                onClick={() => toggleExtraFeature(e.category, f)}
                                className={`px-3 py-1.5 rounded-full text-sm border transition-all ${active ? "bg-[#9E2A3B] text-white border-[#9E2A3B]" : "bg-white text-[#5b4d4f] border-[#F0DDE0] hover:border-[#E7A9B0]"}`}
                              >
                                {f}
                              </button>
                            );
                          })}
                        </div>
                        <div className="flex items-center gap-2 mt-2 max-w-md">
                          <Input
                            data-testid={`extra-custom-feature-input-${e.category}`}
                            value={extraCustomFeature[e.category] || ""}
                            onChange={(ev) => setExtraCustomFeature((p) => ({ ...p, [e.category]: ev.target.value }))}
                            onKeyDown={(ev) => { if (ev.key === "Enter") { ev.preventDefault(); addExtraCustomFeature(e.category); } }}
                            placeholder="Kendi özelliğinizi yazın (örn. Havuz Başı)"
                            className="border-[#F0DDE0] focus-visible:ring-[#C15B6B]"
                          />
                          <button
                            type="button"
                            data-testid={`extra-add-custom-feature-btn-${e.category}`}
                            onClick={() => addExtraCustomFeature(e.category)}
                            className="shrink-0 flex items-center gap-1.5 px-4 h-10 rounded-xl bg-[#9E2A3B] text-white text-sm font-medium hover:bg-[#8a2333] transition-colors"
                          >
                            <Check className="w-4 h-4" /> Ekle
                          </button>
                        </div>
                      </div>

                      <div className="bg-white rounded-2xl border border-[#F0DDE0] p-6">
                        <div className="flex items-center justify-between mb-1">
                          <h3 className="font-display text-xl text-[#2A2224]">Salon Fotoğrafları</h3>
                          <span className={`text-sm font-medium px-3 py-1 rounded-full ${imgs.length >= imgLimit ? "bg-[#FBEEF0] text-[#9E2A3B]" : "bg-[#F3EDEE] text-[#7C6E70]"}`}>{imgs.length} / {imgLimit}</span>
                        </div>
                        <p className="text-sm text-[#7C6E70] mb-4">İlk fotoğraf vitrin (kapak) görseli olarak kullanılır.</p>

                        <input ref={(el) => (extraFileRefs.current[e.category] = el)} type="file" accept="image/*" multiple className="hidden" onChange={(ev) => handleExtraFiles(e.category, ev)} />
                        <button
                          onClick={() => extraFileRefs.current[e.category]?.click()}
                          disabled={extraUploading === e.category || imgs.length >= imgLimit}
                          data-testid={`extra-photo-add-${e.category}`}
                          className="w-full flex flex-col items-center justify-center gap-2 py-8 rounded-xl border-2 border-dashed border-[#E7A9B0] text-[#9E2A3B] hover:bg-[#FBF0F2] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {extraUploading === e.category ? <Loader2 className="w-7 h-7 animate-spin" /> : <ImagePlus className="w-7 h-7" />}
                          <span className="text-sm font-medium">{extraUploading === e.category ? "Yükleniyor..." : imgs.length >= imgLimit ? "Limit doldu" : "Fotoğraf Ekle"}</span>
                          <span className="text-xs text-[#7C6E70]">JPG / PNG • en fazla {imgLimit} adet</span>
                        </button>

                        {imgs.length > 0 && (
                          <SortableGallery images={imgs} onDelete={async (i) => { const r = await axios.delete(`${API}${meBase}/extra-categories/${e.category}/images/${i}`, { headers }); setFirm(r.data); }} onReorder={(order) => reorderImages(order, e.category)} captions={firm?.captions} onCaption={saveCaption} testid={`extra-gallery-${e.category}`} />
                        )}
                        {imgs.length === 0 && (
                          <p className="text-center text-sm text-[#7C6E70] mt-4">Henüz fotoğraf eklemediniz.</p>
                        )}
                      </div>

                      <button onClick={() => saveExtra(e.category)} disabled={extraSaving === e.category}
                        data-testid={`extra-save-${e.category}`}
                        className="w-full flex items-center justify-center gap-2 h-12 rounded-xl bg-gradient-to-r from-[#C15B6B] to-[#9E2A3B] text-white font-medium shadow-md hover:shadow-lg hover:-translate-y-0.5 transition-all disabled:opacity-70">
                        {extraSaving === e.category ? <Loader2 className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />}
                        {extraSaving === e.category ? "Kaydediliyor..." : "Bilgileri Kaydet ve Yayınla"}
                      </button>
                    </div>
                  );
                })}

                {!isPremium ? (
                  <div className="flex items-center gap-2 rounded-xl border border-[#E3CE9E] bg-[#FBF3E4] px-4 py-3">
                    <Lock className="w-4 h-4 text-[#a9843f] shrink-0" />
                    <p className="text-sm text-[#8a6d1f]">Ek kategori satın almak için önce "Ana Sayfa" sekmesinden Premium üyeliğe geçmelisiniz.</p>
                  </div>
                ) : maxReached ? (
                  <div className="flex items-center gap-2 rounded-xl border border-[#E3CE9E] bg-[#FBF3E4] px-4 py-3">
                    <Check className="w-4 h-4 text-[#a9843f] shrink-0" />
                    <p className="text-sm text-[#8a6d1f]">En fazla 3 ek kategori ekleyebilirsiniz. Yeni eklemek için mevcut kategorilerden birinin süresinin dolmasını bekleyin.</p>
                  </div>
                ) : (
                  <>
                  <div className="flex flex-col sm:flex-row sm:items-end gap-3 pt-2 border-t border-[#F3EDEE]">
                    <div className="flex-1 space-y-1.5">
                      <label className="text-sm text-[#5b4d4f]">{nextSlot + 2}. ek kategoriyi seç</label>
                      <Select value={extraCat} onValueChange={setExtraCat}>
                        <SelectTrigger data-testid="extra-cat-select" className="border-[#F0DDE0] h-11"><SelectValue placeholder="Kategori seçin" /></SelectTrigger>
                        <SelectContent>
                          {available.map((c) => <SelectItem key={c.slug} value={c.slug}>{c.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="text-left sm:text-right">
                      {nextPrice > 0 && <p className="font-display text-xl font-bold text-[#9E2A3B]">{fmtPrice(nextPrice)}<span className="text-xs font-normal text-[#7C6E70]"> / {membership.durationMonths} ay</span></p>}
                      <button
                        onClick={() => extraCat && startPayment("category", extraCat, null, discounts.category?.code)}
                        disabled={payBusy || !extraCat || !membership.configured || !nextPrice}
                        data-testid="extra-cat-pay-button"
                        className="mt-1 flex items-center gap-2 px-5 h-11 rounded-xl bg-gradient-to-r from-[#C6A15B] to-[#a9843f] text-white text-sm font-medium hover:shadow-md transition-all disabled:opacity-60">
                        {payBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Crown className="w-4 h-4" />}
                        Öde ve Ekle
                      </button>
                    </div>
                  </div>
                  {nextPrice > 0 && (
                    <DiscountBox API={API} token={token} paymentType="category" basePrice={nextPrice} testid="discount-category"
                      applied={discounts.category} onApply={(v) => setDiscount("category", v)} onClear={() => setDiscount("category", null)} />
                  )}
                  </>
                )}
              </div>
            );
          })()}
        </div>
        )}

        </div>
      </div>

      <Footer />
    </div>
  );
};

export default FirmaPanel;
